import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGenerate } from '@/hooks/useGenerate';
import { useGenerationProgress } from '@/hooks/useGenerationProgress';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import { supabase } from '@/lib/supabase';
import { authFetch } from '@/lib/authFetch';
import { messageCreditsForTier, integrationCreditsForTier } from '@/config/plans';
import { canBuyCreditPacks } from '@/config/pricing';
import { REFERRAL_DISCOUNT } from '@/config/earnings';
import SiteDashboard from '@/components/generation/SiteDashboard';
import GenerationProgressBar from '@/components/generation/GenerationProgressBar';
import LivePreviewFrame from '@/components/LivePreviewFrame';
import VisualEditBar from '@/components/generation/VisualEditBar';
import PlanBlueprint from '@/components/generation/PlanBlueprint';
import { PlanChatCard, EditPlanCard } from '@/components/generation/PlanCards';
import GitHubPanel from '@/components/generation/GitHubPanel';
import DownloadMenu from '@/components/generation/DownloadMenu';
import { MAX_PROMPT, promptCountTone } from '@/components/generation/PromptComposer';
import { select as selectNode } from '@/lib/visualEdit';
import { applyPhotoMapToPages, applyPhotoTokens } from '@/lib/photoMap';
import { stripTrailingArtifacts } from '@/lib/sitePages';
import { buildReactFiles } from '@/lib/reactExport';
import {
  DEFAULT_PAGE, normalizePagePath, toPages, homePath, pageLabel,
  buildStandaloneDoc, buildSiteFiles, iconDataUri, siteSlug, withDrafts,
} from '@/lib/sitePages';
import { formatElapsed, formatDuration, buildTaskList, taskProgress } from '@/lib/generationProgress';
import { resolveStructure, withPlannedPages } from '@/lib/wireframe';
import { describeIssue, fixRequest } from '@/lib/siteIssues';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  readImageAttachment,
  speechRecognitionAvailable,
  startDictation,
  appendTranscript,
  dictationErrorMessage,
} from '@/lib/promptInput';
import { trackCodeExported } from '@/lib/analytics';
import { highlightCode, prismLanguageFor } from '@/lib/highlight';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Bot, Zap, Paperclip, Mic, MicOff, Send,
  AlertTriangle, Maximize2, Code2, Monitor, Tablet, Smartphone,
  LayoutGrid, X, Download, Copy, Check, Clock,
  Image as ImageIcon, Settings2, ExternalLink, FileCode, FileText, Braces, ChevronRight, ChevronDown,
  Folder, FolderOpen,
  Pencil, Gift, BookOpen, HelpCircle, Search, Columns, RefreshCw, Lock, Github,
  Loader2, Wrench, MessagesSquare, PanelLeftClose, PanelLeftOpen,
  MessageSquare, Eye, Ellipsis, Sun, Moon, Link2, ListChecks, CreditCard,
} from 'lucide-react';
import { getPanelElement } from 'react-resizable-panels';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// How long the code editor waits after the last keystroke before autosaving.
// Long enough not to fire mid-word, short enough that a refresh rarely costs
// anything; the beforeunload guard covers the gap.
const AUTOSAVE_DELAY_MS = 1500;

// Whether the chat column is closed, per browser. A convenience only: a
// private window or blocked storage just starts with the chat open.
const CHAT_COLLAPSED_KEY = 'bl_chat_collapsed';
function readChatCollapsed() {
  try { return localStorage.getItem(CHAT_COLLAPSED_KEY) === '1'; } catch { return false; }
}
function writeChatCollapsed(collapsed) {
  try { localStorage.setItem(CHAT_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* not worth failing over */ }
}

// Whether the chat plans a change before making it (plan mode for edits), per
// browser. Off by default: most edits are small, and planning them first adds
// a step.
const EDIT_PLAN_KEY = 'bl_edit_plan_mode';
function readEditPlanMode() {
  try { return localStorage.getItem(EDIT_PLAN_KEY) === '1'; } catch { return false; }
}
function writeEditPlanMode(on) {
  try { localStorage.setItem(EDIT_PLAN_KEY, on ? '1' : '0'); } catch { /* remembered for this page only */ }
}

// The chat column's open/close control. It is rendered in two places — the
// chat's own header while it is open, and the start of the workbench toolbar
// while it is closed — at the same spot on screen, so a second click without
// moving the mouse undoes the first. Desktop only: a phone switches between
// chat and site with its tab bar.
function ChatToggle({ collapsed, onToggle, busy = false }) {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  // A build running out of sight: the Stop button is in the chat, so say that
  // it is worth opening — in the label itself, which is all a screen reader
  // announces once aria-label is set.
  const label = collapsed
    ? (busy ? 'Open chat (a build is running)' : 'Open chat')
    : 'Close chat';
  return (
    <button
      onClick={onToggle}
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
      className="relative shrink-0 flex items-center justify-center w-6 h-6 rounded-lg border border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-800 hover:border-zinc-700 transition-colors"
    >
      <Icon className="w-3.5 h-3.5" />
      {busy && (
        <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 animate-pulse shadow-[0_0_8px_rgba(249,115,22,0.8)]" />
      )}
    </button>
  );
}

// ─── Phone layout ─────────────────────────────────────────────────────────────
// On a phone the workspace is one screen at a time — chat OR the site — with a
// tab bar at the bottom, instead of the desktop's two columns stacked into two
// bands. The bands left the chat about three lines tall: the plan to approve
// sat below its fold while its countdown started the build, and the keyboard
// took what little was left.
//
// Both panes stay mounted and the inactive one is only made invisible, so
// switching never reloads the preview or loses the transcript's place.
// `invisible` also takes the hidden pane out of the tab order and away from
// screen readers. These stand in for ResizablePanelGroup / ResizablePanel
// around the same contents, so there is one chat and one workbench, not two.

function PhoneStack({ children }) {
  return <div className="flex-1 relative overflow-hidden">{children}</div>;
}

function PhonePane({ active, label, className = '', children }) {
  return (
    <section
      aria-label={label}
      className={`absolute inset-0 flex flex-col overflow-hidden ${active ? '' : 'invisible pointer-events-none'} ${className}`}
    >
      {children}
    </section>
  );
}

/** The bottom tab bar. Hidden while typing, when the keyboard needs the room. */
function PhoneTabBar({ tabs, active, onSelect }) {
  return (
    <nav
      aria-label="Workspace"
      className="shrink-0 grid border-t border-white/10 bg-zinc-950 pb-[env(safe-area-inset-bottom)]"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map(({ key, label, icon: Icon, dot }) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          aria-current={active === key ? 'page' : undefined}
          className={`relative h-14 flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
            active === key ? 'text-orange-400' : 'text-zinc-500 active:text-zinc-200'
          }`}
        >
          <span className="relative">
            <Icon className="w-5 h-5" />
            {dot && <span aria-hidden="true" className="absolute -top-0.5 -right-1.5 w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
          </span>
          {label}
        </button>
      ))}
    </nav>
  );
}

/**
 * The build, while the site is on screen. The chat carries the full task list;
 * this is the one line that says it is still going, the way back to that list,
 * and the Stop button, which otherwise lives in the chat's composer.
 */
function PhoneBuildPill({ progress, onOpen, onStop }) {
  const { percent, phase, elapsedMs } = progress;
  return (
    <div className="absolute left-3 right-3 bottom-3 z-30 rounded-2xl bg-zinc-900/95 border border-zinc-800 shadow-[0_12px_40px_rgba(0,0,0,0.6)] backdrop-blur overflow-hidden">
      <div className="h-0.5 bg-zinc-800">
        <div className="h-full bg-orange-500 transition-[width] duration-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex items-center gap-2 pl-3 pr-2 py-2">
        <button onClick={onOpen} className="flex-1 min-w-0 flex items-center gap-2.5 text-left">
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shrink-0" />
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-white truncate">{phase || 'Building your site'}</span>
            <span className="block text-[11px] text-zinc-500 tabular-nums">{percent}% · {formatElapsed(elapsedMs)} · tap for details</span>
          </span>
        </button>
        <button
          onClick={onStop}
          aria-label="Stop generating"
          className="shrink-0 w-10 h-10 rounded-xl bg-zinc-700 active:bg-zinc-600 grid place-items-center"
        >
          <span className="block w-2.5 h-2.5 rounded-[3px] bg-white" />
        </button>
      </div>
    </div>
  );
}

/**
 * What the top bar holds on a desktop and has no room for on a phone. Upgrade
 * and GitHub were simply hidden below `sm`, which made them unreachable for
 * most of the people using the product.
 */
function PhoneMoreMenu({ hasSite, onUpgrade, onCredits, onGitHub }) {
  const item = 'gap-2.5 px-2.5 py-2.5 text-sm text-zinc-200 rounded-xl focus:bg-zinc-800 focus:text-white';
  // The top bar has no room for the theme button on a phone, so it is here.
  const { isDark, toggleTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label="More" className="shrink-0 h-8 w-8 grid place-items-center rounded-lg text-zinc-400 active:text-white active:bg-white/5">
          <Ellipsis className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800 text-zinc-200 rounded-2xl p-1.5">
        <DropdownMenuItem onSelect={onCredits} className={item}><Zap className="text-orange-400" /> Credits &amp; plan</DropdownMenuItem>
        <DropdownMenuItem onSelect={onUpgrade} className={item}><LayoutGrid className="text-zinc-400" /> Upgrade</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem disabled={!hasSite} onSelect={onGitHub} className={item}><Github className="text-zinc-400" /> GitHub</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem onSelect={toggleTheme} className={item}>
          {isDark ? <Sun className="text-zinc-400" /> : <Moon className="text-zinc-400" />}
          {isDark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Text entry that brings up a phone's keyboard. */
function isTextEntry(el) {
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  return el.tagName === 'INPUT' && !/^(checkbox|radio|range|button|submit|file|color)$/i.test(el.type || '');
}

// ─── Credits Panel (Feature 2) ────────────────────────────────────────────────

// It used to show a "Daily Credits" row (a daily allowance this product does
// not have), a form-submission bar fixed at zero, a renewal date of "the first
// of next month" (credits refresh 30 days after the last refresh), and three
// buttons that only said "Coming soon!". Every figure here is real now and
// every item goes somewhere.
const CREDIT_PERIOD_DAYS = 30;

function CreditsPanel({ credits, planLimit, subscriptionTier, profile, onClose, navigate }) {
  const total = planLimit ?? messageCreditsForTier(subscriptionTier);
  const left  = credits ?? total;
  const bought = Math.min(Math.max(profile?.extra_credits || 0, 0), Math.max(left, 0));
  const planLeft = Math.max(left - bought, 0);
  const formsTotal = integrationCreditsForTier(subscriptionTier);
  const formsUsed = Math.min(profile?.integration_credits_used || 0, formsTotal);

  const renews = profile?.last_credit_reset
    ? new Date(new Date(profile.last_credit_reset).getTime() + CREDIT_PERIOD_DAYS * 86400000)
    : null;
  const renewStr = renews ? renews.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const canBuy = canBuyCreditPacks(subscriptionTier);

  const creditRows = [
    { label: 'Plan credits left', value: planLeft, total, color: 'bg-orange-500' },
    { label: 'Form submissions used', value: formsUsed, total: formsTotal, color: 'bg-zinc-500' },
  ];

  const menuItems = [
    canBuy
      ? { icon: CreditCard, label: 'Buy extra credits', action: () => navigate('/dashboard?section=billing') }
      : { icon: LayoutGrid, label: 'Pricing plans', action: () => navigate('/Pricing') },
    { icon: Gift,       label: `Earn: friends get ${REFERRAL_DISCOUNT} off`, action: () => navigate('/dashboard?section=earn') },
    { icon: Settings2,  label: 'Settings',      action: () => navigate('/dashboard?section=account') },
    { icon: BookOpen,   label: 'Guides',        action: () => navigate('/Academy') },
    { icon: HelpCircle, label: 'Get help',      action: () => navigate('/Contact') },
  ];

  return (
    <motion.div
      initial={{ x: '-100%' }}
      animate={{ x: 0 }}
      exit={{ x: '-100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute inset-0 bg-zinc-900 z-30 flex flex-col border-r border-zinc-800 overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-zinc-800 shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Credits left</p>
          <p className="text-3xl font-extrabold text-white tabular-nums mt-1">{credits ?? '…'}</p>
          {bought > 0 && <p className="text-[11px] text-emerald-400 mt-0.5">including {bought} bought — they never expire</p>}
        </div>

        <div className="space-y-4">
          {creditRows.map(row => (
            <div key={row.label}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-white font-medium">{row.label}</span>
                <span className="text-xs text-zinc-400 tabular-nums">{row.value}/{row.total}</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full ${row.color} rounded-full transition-all duration-500`}
                  style={{ width: `${row.total ? Math.min((row.value / row.total) * 100, 100) : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          {renewStr && (
            <p className="text-[11px] text-zinc-500">
              Plan credits refresh on {renewStr}{subscriptionTier !== 'free' ? ' — unused ones roll over (up to a month).' : '.'}
            </p>
          )}
          <button
            onClick={() => navigate(canBuy ? '/dashboard?section=billing' : '/Pricing')}
            className="text-sm text-orange-400 hover:text-orange-300 transition-colors font-medium"
          >
            {canBuy ? 'Buy extra credits' : 'Upgrade your plan'}
          </button>
        </div>

        {/* Menu items */}
        <div className="border-t border-zinc-800 pt-4 space-y-0.5">
          {menuItems.map(({ icon: Icon, label, action }) => (
            <button
              key={label}
              onClick={action}
              className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800 text-sm transition-colors text-left"
            >
              <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Chat History Panel ───────────────────────────────────────────────────────
// Every request sent on this site, newest first, each one a jump to where it
// sits in the transcript. The chat column is ~220px wide and a single build
// fills several screens of it with plan and progress cards, so the only way
// back to "what did I ask for the footer?" was scrolling up through all of
// them. Opens below the pane header rather than over it, so the icon that
// opened it stays visible and closes it again.

function ChatHistoryPanel({ messages, onJump, onClose }) {
  const sent = messages.filter(m => m.role === 'user' && m.content?.trim()).reverse();

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute inset-x-0 top-10 bottom-0 bg-zinc-900 z-30 flex flex-col border-l border-zinc-800 overflow-hidden"
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-800 shrink-0">
        <span className="text-xs font-semibold text-white">
          Your messages
          {sent.length > 0 && <span className="ml-1.5 text-zinc-500 font-normal tabular-nums">{sent.length}</span>}
        </span>
        <button onClick={onClose} aria-label="Close chat history" className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {sent.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center text-zinc-600">
            <MessagesSquare className="w-8 h-8 opacity-30" />
            <p className="text-xs">Nothing sent yet. Your messages will be listed here.</p>
          </div>
        ) : (
          <ul className="p-2 space-y-0.5">
            {sent.map(m => (
              <li key={m.id}>
                <button
                  onClick={() => onJump(m.id)}
                  className="group w-full text-left rounded-lg px-2.5 py-2 hover:bg-zinc-800 transition-colors"
                >
                  <p className="text-[12.5px] text-zinc-300 group-hover:text-white leading-relaxed line-clamp-3 break-words">{m.content}</p>
                  {/* Prompts restored from version history carry no send time. */}
                  <span className="text-[10px] text-zinc-600">{m.timestamp ? formatTime(m.timestamp) : 'Earlier'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

// ─── Publish Panel ────────────────────────────────────────────────────────────
// Publishing now gives the site an address of its own rather than widening
// access to a preview route. Two URLs come back: the path form, which works the
// moment the row is written, and the pretty form (subdomain or the customer's
// own domain), which additionally needs DNS. Both serve the same site.

function PublishPanel({ websiteId, onClose, onAddOrderButtons }) {
  const [publishing, setPublishing] = useState(false);
  const [state, setState]   = useState(null);   // null = loading
  const [copied, setCopied] = useState('');
  const [domain, setDomain] = useState('');
  const [dns, setDns]       = useState(null);
  const [savingDomain, setSavingDomain] = useState(false);
  // Asked for here rather than at signup: this is the first moment the site is
  // going in front of buyers, so "where should orders go?" is the obvious
  // question. Prefilled from the profile, so a second site never asks again.
  const [waNumber, setWaNumber] = useState('');
  const [waSaved, setWaSaved]   = useState(false);

  useEffect(() => {
    if (!websiteId) { setState({ is_public: false }); return; }
    let cancelled = false;
    authFetch(`/api/websites/${websiteId}`)
      .then(({ website }) => {
        if (cancelled) return;
        setState(website);
        setDomain(website.custom_domain || '');
      })
      .catch(() => { if (!cancelled) setState({ is_public: false }); });
    return () => { cancelled = true; };
  }, [websiteId]);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/me')
      .then(({ profile }) => { if (!cancelled) setWaNumber(profile?.whatsapp_number || ''); })
      .catch(() => {});   // prefill is a convenience; publishing must not depend on it
    return () => { cancelled = true; };
  }, []);

  // Opening this panel IS the decision to publish, so it publishes.
  //
  // It used to open on a paragraph explaining what publishing is and a WhatsApp
  // field, and the actual button was below both — two reads and two clicks to
  // do the one thing the panel is named after. The number is still picked up:
  // the server seeds it from the profile on a first publish, and Settings is
  // where it is edited. Guarded by a ref so a re-render
  // mid-request cannot fire a second publish.
  const autoPublishedRef = useRef(false);
  useEffect(() => {
    if (autoPublishedRef.current) return;
    if (!websiteId || state === null || state.is_public) return;
    autoPublishedRef.current = true;
    publish(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, state]);

  const liveUrl = state?.slug ? `${window.location.origin}/s/${state.slug}` : '';
  const prettyUrl = state?.custom_domain
    ? `https://${state.custom_domain}`
    : (state?.deployment_url && !state.deployment_url.startsWith('/') ? state.deployment_url : '');

  const publish = async (next) => {
    if (!websiteId) { toast.error('Generate a website first.'); return; }
    setPublishing(true);
    try {
      const res = await authFetch(
        `/api/websites/${websiteId}/${next ? 'publish' : 'unpublish'}`,
        next
          ? { method: 'POST', body: JSON.stringify({ whatsappNumber: waNumber.trim() }) }
          : { method: 'POST' },
      );
      setState(cur => ({
        ...(cur || {}),
        is_public: next,
        slug: res.slug ?? cur?.slug,
        deployment_url: res.url ?? cur?.deployment_url,
      }));
      if (next && res.whatsapp) setWaSaved(true);
      toast.success(next ? 'Your site is live.' : 'Site unpublished.');
    } catch (e) { toast.error(e.message); }
    finally { setPublishing(false); }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const res = await authFetch(`/api/websites/${websiteId}/domain`, {
        method: 'PATCH',
        body: JSON.stringify({ custom_domain: domain.trim() || null }),
      });
      setDns(res.dns);
      setState(cur => ({ ...(cur || {}), custom_domain: res.custom_domain }));
      toast.success(res.custom_domain ? 'Domain saved — now point its DNS here.' : 'Domain removed.');
    } catch (e) { toast.error(e.message); }
    finally { setSavingDomain(false); }
  };

  const copy = (value, key) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      toast.success('Copied.');
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const UrlRow = ({ label, value, hint, k }) => (
    <div>
      <label className="text-xs text-zinc-500 mb-1.5 block">{label}</label>
      <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2">
        <span className="text-xs text-zinc-300 flex-1 truncate">{value}</span>
        <button
          onClick={() => copy(value, k)}
          className="text-xs text-orange-400 hover:text-orange-300 shrink-0 font-medium"
        >
          {copied === k ? 'Copied' : 'Copy'}
        </button>
      </div>
      {hint && <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">{hint}</p>}
    </div>
  );

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute inset-y-0 right-0 w-80 bg-zinc-950 border-l border-zinc-800 z-20 flex flex-col shadow-2xl"
    >
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-base font-bold text-white">Share</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
        {state?.is_public ? (
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3.5 py-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
            <p className="text-emerald-300 text-sm font-medium">Live on the internet</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl px-3.5 py-3">
            <Loader2 className="w-4 h-4 text-orange-400 animate-spin shrink-0" />
            <p className="text-zinc-300 text-sm font-medium">Putting your site online…</p>
          </div>
        )}

        {waSaved && onAddOrderButtons && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3.5">
            <p className="text-emerald-200 text-xs leading-relaxed mb-2.5">
              Your number is saved. This site was built before we knew it, so its
              buttons do not order yet — add them now and every product links to
              your WhatsApp.
            </p>
            <Button
              onClick={() => {
                onAddOrderButtons('Add WhatsApp order buttons: turn every product, package, price and primary call-to-action into an order link, following the ordering rules for this business. Do not change the design.');
                setWaSaved(false);
                onClose();
              }}
              className="w-full h-9 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg text-xs font-semibold"
            >
              Add order buttons to this site
            </Button>
          </div>
        )}

        {/* !!: MySQL's 0 for "not public" would otherwise print as "0". */}
        {!!state?.is_public && liveUrl && (
          <>
            <UrlRow label="Your link" value={liveUrl} k="path" />

            {prettyUrl && prettyUrl !== liveUrl && (
              <UrlRow
                label={state.custom_domain ? 'Your domain' : 'Short address'}
                value={prettyUrl}
                k="pretty"
                hint={state.custom_domain
                  ? 'Live once this domain points here and its certificate is issued.'
                  : 'Works once wildcard DNS is set up for the site domain.'}
              />
            )}

            <Button
              variant="outline"
              onClick={() => window.open(liveUrl, '_blank', 'noopener')}
              className="w-full h-10 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-xl text-sm gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Visit site
            </Button>

            <div className="pt-2 border-t border-zinc-800">
              <label className="text-xs text-zinc-500 mb-1.5 block">Use your own domain</label>
              <div className="flex gap-2">
                <input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="yourbusiness.com"
                  className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-orange-500"
                />
                <Button
                  onClick={saveDomain}
                  disabled={savingDomain}
                  className="h-[34px] px-3 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg border border-zinc-700"
                >
                  {savingDomain ? '…' : 'Save'}
                </Button>
              </div>
              {dns?.cname && (
                <div className="mt-2 bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                  <p className="text-[11px] text-zinc-400 mb-1.5">Add this DNS record at your registrar:</p>
                  <p className="font-mono text-[11px] text-zinc-300 break-all">
                    CNAME {dns.cname.name} → {dns.cname.value}
                  </p>
                  <p className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">{dns.note}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Only ever a way back off the internet: getting on it happened when the
          panel opened. A failed publish still needs a retry, though — an
          auto-publish that 409s on an unsaved site must not leave a dead end. */}
      <div className="p-5 border-t border-zinc-800 shrink-0">
        <Button
          onClick={() => publish(!state?.is_public)}
          disabled={publishing || !websiteId || state === null}
          className={state?.is_public
            ? 'w-full h-11 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-sm font-semibold border border-zinc-700'
            : 'w-full h-11 bg-orange-500 hover:bg-orange-400 text-ink rounded-xl text-sm font-semibold shadow-[0_0_16px_rgba(249,115,22,0.25)]'}
        >
          {publishing ? 'Working…' : state?.is_public ? 'Unpublish' : 'Try again'}
        </Button>
      </div>
    </motion.div>
  );
}

// ─── Chat Bubble Components ───────────────────────────────────────────────────

function UserBubble({ msgId, content, timestamp, userInitial, highlighted }) {
  return (
    <motion.div
      data-msg-id={msgId}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex items-end gap-2.5 justify-end min-w-0 overflow-hidden"
    >
      <div className="flex flex-col items-end gap-1 min-w-0 max-w-[calc(100%-44px)]">
        {/* `highlighted` marks the message just jumped to from chat history —
            in a column of look-alike grey bubbles, landing near it is not
            the same as finding it. Inset, because the row clips overflow. */}
        <div className={`bg-zinc-800 text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[13px] leading-relaxed break-words overflow-hidden w-full transition-shadow duration-500 ${
          highlighted ? 'ring-2 ring-inset ring-orange-500/70' : ''
        }`}>
          {content}
        </div>
        {/* Prompts restored from version history have no reliable send time —
            show nothing rather than "Invalid Date". */}
        {timestamp && (
          <span className="text-zinc-600 text-[11px] px-1 shrink-0">{formatTime(timestamp)}</span>
        )}
      </div>
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-[11px] font-bold shrink-0 mb-5 shadow-[0_0_12px_rgba(249,115,22,0.3)]">
        {userInitial}
      </div>
    </motion.div>
  );
}

function AIBubble({ content, timestamp }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex items-end gap-2.5 min-w-0 overflow-hidden"
    >
      <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 mb-5">
        <Bot className="w-3.5 h-3.5 text-orange-400" />
      </div>
      <div className="flex flex-col gap-1 min-w-0 max-w-[calc(100%-44px)]">
        <div className="bg-zinc-900 border border-zinc-800 text-zinc-300 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-[12.5px] leading-relaxed font-mono whitespace-pre-wrap break-words overflow-hidden w-full">
          {content}
        </div>
        {timestamp && (
          <span className="text-zinc-600 text-[11px] px-1 shrink-0">{formatTime(timestamp)}</span>
        )}
      </div>
    </motion.div>
  );
}

// ─── Stream parsing ───────────────────────────────────────────────────────────
// The server streams a delimiter format rather than JSON — ===TITLE===, one
// ===PAGE <filename>=== per page, then a shared ===CSS=== and ===JS=== (see
// builderslab/server/services/generator.js). ===HTML=== is the pre-multi-page
// spelling of index.html and is still accepted, both for stored generations and
// for the patch path, which re-emits in that form.
//
// Parsing must tolerate partial input: mid-stream the last section has no
// closing delimiter, so it simply runs to the end of what has arrived so far.

// OUTLINE is the first build's list of pages and sections, written before the
// title so the preview can draw the site's frame (src/lib/wireframe.js). It is a
// delimiter here and on the server so it can never bleed into the section after
// it; the server then discards it, and nothing saved or published contains it.
const SECTION_RE =
  /^[\t ]*={3,}[ \t]*(?:PAGE[:\s][ \t]*([^\n=]+?)|(TITLE|ICON|HTML|CSS|JS|OUTLINE))[ \t]*={3,}[\t ]*$/gm;

/** Pull the <svg> out of an ICON section, ignoring anything around it. */
function extractSvg(text) {
  const m = String(text || '').match(/<svg[\s\S]*<\/svg>/i);
  return m ? m[0].trim() : '';
}


// Exported for the parser-agreement test. The server's parseDelimited reads the
// same stream, and a divergence between them is invisible until a site renders
// differently in the preview than it does when published.
// The fullscreen dialog's own close button, restyled from here.
//
// shadcn renders it as `absolute right-4 top-4 rounded-sm opacity-70` — a bare
// glyph with no background. Over a website that is the whole point of the view,
// it lands on whatever the page happens to show there: against this store's
// dark hero photograph it was invisible, and the only way out people found was
// the pill at the bottom.
//
// Targeted with `[&>button]` rather than by editing the component, because
// src/components/ui/** is shadcn-generated and not hand-edited. The selector is
// safe: the only direct-child button of DialogContent is that close, since the
// preview and the bottom pill are nested inside a wrapper div.
const FULLSCREEN_CLOSE =
  '[&>button]:z-50 [&>button]:h-10 [&>button]:w-10 [&>button]:rounded-full ' +
  '[&>button]:flex [&>button]:items-center [&>button]:justify-center ' +
  '[&>button]:bg-zinc-950/85 [&>button]:text-white [&>button]:opacity-100 ' +
  '[&>button]:shadow-xl [&>button]:ring-1 [&>button]:ring-white/25 ' +
  '[&>button]:backdrop-blur-sm [&>button:hover]:bg-zinc-800 ' +
  '[&>button>svg]:h-5 [&>button>svg]:w-5';

export function parseStream(text) {
  const out = { title: '', icon: '', pages: [], html: '', css: '', js: '', outline: '', outlineDone: false };
  if (!text) return out;

  const marks = [];
  SECTION_RE.lastIndex = 0;
  let m;
  while ((m = SECTION_RE.exec(text)) !== null) {
    marks.push({
      page: m[1] ? normalizePagePath(m[1]) : null,
      key:  m[2] ? m[2].toLowerCase() : null,
      from: m.index,
      to:   m.index + m[0].length,
    });
  }

  if (marks.length) {
    const pages = new Map();

    marks.forEach((mark, i) => {
      const end = i + 1 < marks.length ? marks[i + 1].from : text.length;
      const content = text.slice(mark.to, end).trim();

      if (mark.page) { pages.set(mark.page, content); return; }
      // 'html' is the home page under its old name.
      if (mark.key === 'html') { if (!pages.has(DEFAULT_PAGE)) pages.set(DEFAULT_PAGE, content); return; }
      // Only accept the icon once it has closed, so a half-written <svg> never
      // reaches the favicon link mid-stream.
      if (mark.key === 'icon') { out.icon = extractSvg(content); return; }
      // CSS arrives in two blocks — the design system before the markup and the
      // rest after it — and they are JOINED, not replaced. Assigning here let
      // the second block win, so the preview lost every custom property, the
      // font import and all the tag-level base rules while the published site
      // kept them. parseDelimited on the server does the same thing; the two
      // must agree or a site renders differently in the preview than it does
      // on its own URL.
      if (mark.key === 'css') { out.css = out.css ? `${out.css}\n${content}` : content; return; }
      if (mark.key === 'js') { out.js = stripTrailingArtifacts(content); return; }
      // Complete only once the next section has begun — a half-written line
      // would otherwise draw a section called "Featured coll" for one chunk.
      if (mark.key === 'outline') { out.outline = content; out.outlineDone = i + 1 < marks.length; return; }
      out[mark.key] = content;
    });

    out.pages = [...pages.entries()]
      .map(([path, html]) => ({ path, html }))
      .sort((a, b) => (a.path === DEFAULT_PAGE ? -1 : b.path === DEFAULT_PAGE ? 1 : 0));
    out.html = pages.get(DEFAULT_PAGE) ?? (out.pages[0]?.html || '');
    return out;
  }

  // ── Legacy JSON payloads (pre-delimiter generations) ────────────────────────
  const fromJson = (title, html, css, js) => ({
    title: title || '',
    icon:  '',
    pages: html ? [{ path: DEFAULT_PAGE, html }] : [],
    html:  html || '',
    css:   css || '',
    js:    js || '',
  });

  try {
    const parsed = JSON.parse(text);
    return fromJson(parsed.title, parsed.html, parsed.css, parsed.js);
  } catch {
    const extractField = (field) => {
      const s1 = `"${field}":"`, s2 = `"${field}": "`;
      let idx = text.indexOf(s1), pad = s1.length;
      if (idx === -1) { idx = text.indexOf(s2); pad = s2.length; }
      if (idx === -1) return '';
      return text.substring(idx + pad)
        .replace(/",\s*"[^"]*"?$/, '').replace(/"?\}?\s*$/, '')
        .replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\');
    };
    return fromJson(
      extractField('title'), extractField('html'),
      extractField('css'),   extractField('js'),
    );
  }
}

// ─── Generation Plan View ─────────────────────────────────────────────────────

function GenerationPlanView({ progress, prompt, plan }) {
  const { percent, elapsedMs, phase, phaseKey, filesDone } = progress;
  const isDone = phaseKey === 'done';

  // Where the stream has got to, as an ordering, so each step can say whether it
  // is finished, running, or still ahead.
  // One row per real unit of work, ticked off as the stream reaches it. With an
  // approved plan every row is on screen before the first token arrives.
  const tasks = buildTaskList(progress, plan);
  const { done: tasksDone, total: tasksTotal } = taskProgress(tasks);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 mt-1">
        <Bot className="w-3.5 h-3.5 text-orange-400" />
      </div>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1 overflow-hidden">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl rounded-bl-sm px-3.5 py-3">
          <p className="text-zinc-300 text-[13px] leading-relaxed mb-2.5 break-words">
            I'll plan out <span className="text-white font-medium">"{prompt}"</span> and then build it. Let me think through the design and structure:
          </p>

          {/* Progress header — the same numbers as the workspace strip, so the
              chat can be read on its own without hunting for the top bar. */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-[11px] font-medium ${isDone ? 'text-emerald-400' : 'text-orange-300'}`}>
                {isDone ? 'Done' : phase}
              </span>
              <span className="text-[11px] text-zinc-500 tabular-nums">
                {tasksDone}/{tasksTotal} · {percent}% · {formatElapsed(elapsedMs)}
              </span>
            </div>
            <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-700 ease-out ${isDone ? 'bg-emerald-500' : 'bg-gradient-to-r from-orange-600 to-orange-400'}`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            {tasks.map(task => (
              <div key={task.key} className="flex items-start gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 ${
                  task.status === 'done' ? 'bg-emerald-500/20 border border-emerald-500/40'
                    : task.status === 'active' ? 'bg-orange-500/20 border border-orange-500/40'
                    : 'bg-zinc-800 border border-zinc-700'
                }`}>
                  {task.status === 'done' ? <Check className="w-2.5 h-2.5 text-emerald-400" />
                    : task.status === 'active' ? <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                    : <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />}
                </div>
                <div className="min-w-0">
                  <p className={`text-[11.5px] font-medium leading-snug break-words ${
                    task.status === 'done' ? 'text-emerald-400/90 line-through decoration-emerald-500/30'
                      : task.status === 'active' ? 'text-orange-300'
                      : 'text-zinc-500'
                  }`}>
                    {task.label}
                    {task.status === 'active' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse ml-1.5 -mb-px" />}
                  </p>
                  <p className="text-[10.5px] leading-snug text-zinc-500 mt-px break-words">{task.detail}</p>
                </div>
              </div>
            ))}
          </div>
          {filesDone.length > 0 && (
            <div className="mt-2.5 pt-2.5 border-t border-zinc-800 flex flex-wrap gap-1">
              {filesDone.map(f => (
                <div key={f} className="flex items-center gap-1 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5 text-[10px] text-zinc-400">
                  <FileCode className="w-2.5 h-2.5 text-orange-400" />
                  Wrote {f}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Edit Progress View ───────────────────────────────────────────────────────
// What an edit shows while it runs. It used to be the first build's card: "I'll
// plan out "<the prompt the site was first built from>" and then build it",
// over a checklist of designing the system, naming the site and writing every
// page. That described a rebuild, which an edit is not, and it quoted a request
// from the first build instead of the one just sent.
//
// An edit has two honest states (readGenerationProgress, mode). Usually the
// server asks the model for the change alone and sends the site back once it
// is applied, so there is nothing to tick off — only that it is working, and
// for how long. If the change cannot be made that way, the server says so and
// writes the site out again; that streams like a build and takes minutes, and
// the card says why it is slower.

function EditProgressView({ progress, request }) {
  const { percent, elapsedMs, phase, detail, phaseKey, mode } = progress;
  const isDone = phaseKey === 'done';
  const rewriting = mode === 'rewrite';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 mt-1">
        <Bot className="w-3.5 h-3.5 text-orange-400" />
      </div>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1 overflow-hidden">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl rounded-bl-sm px-3.5 py-3">
          <p className="text-zinc-300 text-[13px] leading-relaxed mb-2.5 break-words">
            Updating your site: <span className="text-white font-medium">"{request}"</span>
          </p>

          {rewriting && !isDone && (
            <p className="text-[11.5px] text-zinc-400 leading-snug mb-2.5 break-words">
              This change could not be made as a small edit, so the whole site is being written out again. That takes a few minutes.
            </p>
          )}

          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className={`text-[11px] font-medium ${isDone ? 'text-emerald-400' : 'text-orange-300'}`}>
              {phase}
            </span>
            <span className="text-[11px] text-zinc-500 tabular-nums">
              {rewriting ? `${percent}% · ` : ''}{formatElapsed(elapsedMs)}
            </span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-[width] duration-700 ease-out ${isDone ? 'bg-emerald-500' : 'bg-gradient-to-r from-orange-600 to-orange-400'}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="text-[10.5px] leading-snug text-zinc-500 mt-1.5 break-words">{detail}</p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Building Skeleton (preview while generating) ────────────────────────────
// A wireframe page that "assembles" in the preview area, phase-aware via the
// generation stream, so the wait feels alive instead of a blank panel.

function BuildingSkeleton({ progress, isGenerating }) {
  const { percent, phase, detail, elapsedMs } = progress;

  const block = 'rounded-lg bg-zinc-800/80 relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-zinc-700/40 before:to-transparent before:animate-[shimmer_1.8s_infinite]';

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-6 p-8">
      {/* Wireframe canvas */}
      <div className="w-full max-w-2xl bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-4 shadow-[0_0_60px_rgba(249,115,22,0.04)]">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 pb-3 border-b border-zinc-800/80">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          <div className={`${block} h-5 flex-1 ml-3 max-w-[240px]`} />
        </div>
        {/* Nav */}
        <div className="flex items-center justify-between">
          <div className={`${block} h-6 w-24`} />
          <div className="flex gap-2">
            <div className={`${block} h-5 w-14`} />
            <div className={`${block} h-5 w-14`} />
            <div className={`${block} h-6 w-20 !bg-orange-500/20`} />
          </div>
        </div>
        {/* Hero */}
        <div className="space-y-2.5 py-3">
          <div className={`${block} h-8 w-3/4 mx-auto`} />
          <div className={`${block} h-4 w-1/2 mx-auto`} />
          <div className={`${block} h-9 w-32 mx-auto mt-2 !bg-orange-500/20`} />
        </div>
        {/* Cards */}
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="space-y-2">
              <div className={`${block} h-20`} style={{ animationDelay: `${i * 150}ms` }} />
              <div className={`${block} h-3 w-4/5`} />
              <div className={`${block} h-3 w-3/5`} />
            </div>
          ))}
        </div>
      </div>

      {/* Status */}
      <div className="flex flex-col items-center gap-2.5 w-full max-w-xs">
        <div className="flex items-center gap-2 text-sm text-zinc-300">
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.8)]" />
          {isGenerating ? `${phase}…` : 'Waiting for generation…'}
        </div>
        <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full transition-[width] duration-700 ease-out" style={{ width: `${isGenerating ? percent : 0}%` }} />
        </div>
        {isGenerating && (
          <div className="flex items-center justify-between w-full text-[11px] tabular-nums">
            <span className="text-zinc-500 truncate pr-2">{detail}</span>
            <span className="text-orange-400 font-semibold shrink-0">{percent}% · {formatElapsed(elapsedMs)}</span>
          </div>
        )}
        <p className="text-[11px] text-zinc-600 text-center">Your preview appears here the moment the first sections are ready</p>
      </div>
    </div>
  );
}

// ─── Code Tab (Features 5 & 6) ────────────────────────────────────────────────

/** One file in the tree. Indented by `depth` so a folder's contents read as inside it. */
function FileRow({ file, depth, selected, dirty, onSelect }) {
  const { key, label, icon: Icon } = file;
  return (
    <button
      onClick={() => onSelect(key)}
      aria-current={selected ? 'true' : undefined}
      style={{ paddingLeft: 12 + depth * 18 }}
      className={`w-full flex items-center gap-2.5 pr-3 py-2 text-sm transition-colors ${
        selected ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
      }`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${selected ? 'text-orange-400' : 'text-zinc-600'}`} />
      <span className="truncate">{label}</span>
      {dirty && (
        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" title="Unsaved changes" />
      )}
    </button>
  );
}

function CodeTab({ websiteCode, isGenerating, isPaidPlan, canExport, onBuySite, buyingSite, sitePrice, planPrice, unlock, chatToggle, navigate, onCopy, copySuccess, onDownloadZip, websiteId, onSaveCode, drafts, setDrafts, saveState }) {
  const [selectedFile, setSelectedFile] = useState('html');
  const [splitView, setSplitView] = useState(false);
  // Folders start open. Keyed by name so adding a folder later needs no migration.
  const [collapsed, setCollapsed] = useState({});
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');

  // `drafts` (unsaved edits, keyed by the file key the tree uses) lives in the
  // parent: this component unmounts whenever the right-hand tab changes, so
  // holding them here would discard someone's typing the moment they glanced at
  // the preview. Autosave runs in the parent for the same reason.
  //
  // Editing is meaningless mid-stream — the generator overwrites the buffer on
  // every chunk — and is a paid feature. Both conditions gate the textarea.
  const canEdit  = isPaidPlan && !isGenerating && !!websiteId;
  const isDirty  = Object.keys(drafts).length > 0;
  const saving   = saveState?.status === 'saving';

  const pages = toPages(websiteCode);

  // One entry per generated page, then the two shared assets. Page keys are the
  // filename itself so the copy handler can find them again.
  const files = [
    ...pages.map(p => ({
      key: p.path === DEFAULT_PAGE ? 'html' : p.path,
      label: p.path,
      icon: FileCode,
      content: p.html,
      pagePath: p.path,
      // The home page sits at the root, the way it does in a real project; the
      // rest live under pages/.
      folder: p.path === DEFAULT_PAGE ? '' : 'pages',
    })),
    { key: 'css',  label: 'style.css',  icon: FileText, content: websiteCode.css, folder: 'assets' },
    { key: 'js',   label: 'script.js',  icon: Braces,   content: websiteCode.js,  folder: 'assets' },
    ...(websiteCode.icon
      ? [{ key: 'icon', label: 'favicon.svg', icon: ImageIcon, content: websiteCode.icon, folder: 'assets' }]
      : []),
    // An edited draft always wins over the generated source.
  ].map(f => (drafts[f.key] !== undefined ? { ...f, content: drafts[f.key] } : f));

  // Grouped for display only. `files` stays the flat source of truth, so the
  // copy handler, the autosave and the draft dots all keep addressing files by
  // the same key they always did.
  const q = query.trim().toLowerCase();
  const shown = q ? files.filter(f => f.label.toLowerCase().includes(q)) : files;

  const rootFiles = shown.filter(f => !f.folder);
  const folders = Object.entries(
    shown.reduce((acc, f) => {
      if (!f.folder) return acc;
      (acc[f.folder] ||= []).push(f);
      return acc;
    }, {}),
  );
  const noMatches = q && !shown.length;

  const activeFile = files.find(f => f.key === selectedFile) ?? files[0];
  const lines = (activeFile?.content || '').split('\n');
  const lineCount = lines.length;
  const FREE_PREVIEW_LINES = 15;
  const isTruncated = !canExport && lineCount > FREE_PREVIEW_LINES;
  const visibleLines = isTruncated ? lines.slice(0, FREE_PREVIEW_LINES) : lines;
  const visibleContent = isTruncated ? visibleLines.join('\n') : (activeFile?.content || '');

  // Highlighting runs on every keystroke, so memoise it — Prism tokenising a
  // few thousand lines on each character would be felt while typing.
  const highlighted = useMemo(
    () => highlightCode(visibleContent, prismLanguageFor(activeFile?.key)),
    [visibleContent, activeFile?.key],
  );

  // The highlighted layer is not scrollable itself; it follows the textarea so
  // the two stay registered while typing past the viewport edge.
  const highlightRef = useRef(null);
  const syncScroll = (e) => {
    const el = highlightRef.current;
    if (!el) return;
    el.scrollTop  = e.currentTarget.scrollTop;
    el.scrollLeft = e.currentTarget.scrollLeft;
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* File tree */}
      {/* Not on a phone: beside the code it left each ~190px, which is room
          for neither. The header's file picker stands in for it there. */}
      <div className="hidden md:flex w-48 shrink-0 border-r border-white/5 bg-zinc-950 flex-col overflow-hidden">
        <div className="px-3 pt-2 pb-1 shrink-0">
          <div className="flex items-center justify-between">
            {/* The reopen control while the chat is closed — the same spot the
                preview toolbar puts it, so it does not move between tabs. */}
            <div className="flex items-center gap-2 min-w-0">
              {chatToggle || null}
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest font-semibold">Code files</p>
            </div>
            <button
              onClick={() => { setSearching(v => !v); if (searching) setQuery(''); }}
              aria-label={searching ? 'Close file search' : 'Search files'}
              className={`p-0.5 transition-colors ${searching ? 'text-orange-400' : 'text-zinc-600 hover:text-zinc-300'}`}
            >
              <Search className="w-3 h-3" />
            </button>
          </div>
          {searching && (
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); setSearching(false); } }}
              placeholder="Filter files…"
              className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-[11px] text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-orange-500"
            />
          )}
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-2">
          {rootFiles.map(f => (
            <FileRow
              key={f.key} file={f} depth={0}
              selected={selectedFile === f.key} dirty={drafts[f.key] !== undefined}
              onSelect={setSelectedFile}
            />
          ))}

          {noMatches && (
            <p className="px-3 py-3 text-[11px] text-zinc-600 leading-relaxed">
              Nothing matches “{query.trim()}”.
            </p>
          )}

          {folders.map(([name, entries]) => {
            const open = q ? true : !collapsed[name];
            // A folder holding the open file must never be collapsed out of
            // view — the tree would show no selection at all.
            const holdsSelection = entries.some(f => f.key === selectedFile);
            return (
              <div key={name}>
                <button
                  onClick={() => { if (!q) setCollapsed(c => ({ ...c, [name]: open })); }}
                  aria-expanded={open}
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                >
                  <ChevronRight
                    className={`w-3 h-3 shrink-0 text-zinc-600 transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                  {open
                    ? <FolderOpen className="w-4 h-4 shrink-0 text-zinc-500" />
                    : <Folder className="w-4 h-4 shrink-0 text-zinc-600" />}
                  <span className="truncate">{name}</span>
                  <span className="ml-auto text-[10px] text-zinc-600 tabular-nums">{entries.length}</span>
                </button>

                {(open || holdsSelection) && entries.map(f => (
                  <FileRow
                    key={f.key} file={f} depth={1}
                    selected={selectedFile === f.key} dirty={drafts[f.key] !== undefined}
                    onSelect={setSelectedFile}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Code viewer + optional split preview */}
      <div className="flex-1 flex overflow-hidden">
        {/* Code viewer */}
        <div className={`flex flex-col overflow-hidden ${splitView ? 'w-1/2 border-r border-white/5' : 'flex-1'}`}>
          {/* Header (Feature 6: line count + download) */}
          <div className="h-11 md:h-9 shrink-0 flex items-center justify-between gap-2 px-3 md:px-4 border-b border-white/5 bg-zinc-950/80">
            {/* The file list, as a native picker on a phone. */}
            <label className="md:hidden relative min-w-0 flex items-center">
              <span className="sr-only">File</span>
              <select
                value={activeFile?.key}
                onChange={(e) => setSelectedFile(e.target.value)}
                className="appearance-none min-w-0 max-w-[45vw] truncate bg-zinc-800 text-white text-base rounded-lg pl-2.5 pr-7 py-1 border border-zinc-700 focus:outline-none focus:border-zinc-500"
              >
                {files.map(f => (
                  <option key={f.key} value={f.key}>{f.folder ? `${f.folder}/` : ''}{f.label}{drafts[f.key] !== undefined ? ' •' : ''}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 w-3.5 h-3.5 text-zinc-400" />
            </label>
            <span className="hidden md:inline text-xs text-zinc-400 truncate">
              src / {activeFile?.label}
              {isDirty && <span className="text-orange-400 ml-1.5">•</span>}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {/* Autosave status. "Unsaved" only appears once editing is
                  actually possible, so free users are not told their changes
                  are pending when they cannot make any. */}
              {canEdit && (
                <span className={`text-[10px] ${
                  saveState?.status === 'error' ? 'text-red-400'
                  : saving                      ? 'text-zinc-400'
                  : isDirty                     ? 'text-amber-400'
                  : saveState?.status === 'saved' ? 'text-emerald-400'
                  : 'text-transparent'
                }`}>
                  {saveState?.status === 'error' ? (saveState.message || 'Save failed')
                    : saving   ? 'Saving…'
                    : isDirty  ? 'Unsaved'
                    : saveState?.status === 'saved' ? 'Saved' : '·'}
                </span>
              )}
              {canEdit && isDirty && !saving && (
                <button
                  onClick={onSaveCode}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-orange-500 hover:bg-orange-400 text-ink text-[11px] font-semibold transition-all"
                  title="Save now (autosaves shortly after you stop typing)"
                >
                  Save now
                </button>
              )}
              {activeFile?.content && (
                // Bare numbers here read as an HTTP status — a 404-line file
                // looked like a failed request. The unit disambiguates it.
                <span
                  className="hidden sm:inline text-[10px] text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded font-mono"
                  title={`${lineCount} lines`}
                >
                  {lineCount}L
                </span>
              )}
              <DownloadMenu onDownload={onDownloadZip} canExport={canExport} unlock={unlock}>
                <button
                  disabled={!websiteCode.html}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700 hover:bg-zinc-800 data-[state=open]:text-white data-[state=open]:bg-zinc-800 text-[11px] font-medium transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  <Download className="w-3 h-3" /> Download <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </DownloadMenu>
              {/* Split view toggle (Feature 5). Not on a phone: two halves of
                  a 390px screen is a preview and a listing nobody can read. */}
              <button
                onClick={() => setSplitView(v => !v)}
                title={splitView ? 'Exit split view' : 'Split view'}
                className={`hidden md:inline-flex transition-colors ${splitView ? 'text-orange-400' : 'text-zinc-500 hover:text-white'}`}
              >
                <Columns className="w-3.5 h-3.5" />
              </button>
              {canExport && activeFile?.content && (
                <button
                  onClick={() => onCopy(activeFile.label, activeFile.content)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white text-[11px] transition-all"
                >
                  {copySuccess === activeFile.label ? <><Check className="w-3 h-3 text-emerald-400" />Copied!</> : <><Copy className="w-3 h-3" />Copy</>}
                </button>
              )}
            </div>
          </div>

          {/* Paid-plan notice */}
          {!canExport && (
            <div className="mx-3 md:mx-4 mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 shrink-0">
              <span className="text-xs text-amber-300">Code editing is only available on paid plans.</span>
              <button onClick={() => navigate('/Pricing')} className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2 shrink-0">
                Upgrade your plan
              </button>
            </div>
          )}

          {/* Code with line numbers (Feature 6) */}
          <div className="flex-1 overflow-auto custom-scrollbar relative">
            {activeFile?.content ? (
              <>
                <div className="flex min-h-full">
                  {/* Line numbers */}
                  <div className="flex-shrink-0 border-r border-zinc-800 bg-zinc-950/50 select-none sticky left-0">
                    {visibleLines.map((_, i) => (
                      <div key={i} className="px-3 text-[11px] text-zinc-600 leading-relaxed font-mono text-right min-w-[2.5rem]">
                        {i + 1}
                      </div>
                    ))}
                  </div>
                  {/* Code. Read-only view is the highlighted <pre> on its own;
                      the editor lays a transparent textarea over that same
                      <pre>, so what you type and what is coloured are one
                      layout. Every type metric below is shared by both layers —
                      changing padding, font or leading on one without the other
                      makes the caret drift away from the glyphs. */}
                  <div className="flex-1 relative min-w-0">
                    <pre
                      ref={highlightRef}
                      aria-hidden="true"
                      className={`bl-code-layer m-0 p-4 pl-3 text-xs leading-relaxed font-mono text-zinc-300 ${
                        canEdit ? 'absolute inset-0 overflow-hidden pointer-events-none' : 'overflow-auto custom-scrollbar'
                      }`}
                    >
                      <code dangerouslySetInnerHTML={{ __html: highlighted }} />
                    </pre>
                    {canEdit && (
                      <textarea
                        value={activeFile.content || ''}
                        onChange={(e) => setDrafts(d => ({ ...d, [activeFile.key]: e.target.value }))}
                        onScroll={syncScroll}
                        spellCheck={false}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        wrap="off"
                        aria-label={`Edit ${activeFile.label}`}
                        className="bl-code-layer absolute inset-0 w-full h-full m-0 p-4 pl-3 text-xs leading-relaxed font-mono bg-transparent text-transparent caret-orange-400 border-0 outline-none resize-none overflow-auto custom-scrollbar"
                      />
                    )}
                  </div>
                </div>

                {isTruncated && (
                  <div className="sticky bottom-0 left-0 right-0 -mt-24 pointer-events-none">
                    <div className="h-24 bg-gradient-to-t from-zinc-950 via-zinc-950/90 to-transparent" />
                    <div className="bg-zinc-950 border-t border-zinc-800 px-4 py-5 flex flex-col items-center gap-3 pointer-events-auto">
                      <div className="flex items-center gap-2 text-zinc-300 text-sm">
                        <Lock className="w-4 h-4 text-orange-400" />
                        <span>Showing {FREE_PREVIEW_LINES} of {lineCount} lines — full source is locked on the Free plan.</span>
                      </div>
                      {/* Two ways to unlock, presented as a choice rather than
                          an offer and a consolation link. The one-off is the
                          filled button because it is the cheaper yes and the one
                          this moment is about; the plan sits beside it as an
                          equal, outlined, for the person building more than one
                          site. Both carry a price so neither is a leap of faith,
                          and they stack on a narrow panel. */}
                      <div className="w-full max-w-[420px] flex flex-col sm:flex-row gap-2.5">
                        <button
                          onClick={onBuySite}
                          disabled={buyingSite || !websiteId}
                          aria-label={`Buy this site outright${sitePrice ? ` for ${sitePrice}` : ''}`}
                          className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed text-ink shadow-[0_0_20px_rgba(249,115,22,0.28)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
                        >
                          <span className="block text-xs font-semibold leading-tight">
                            {buyingSite
                              ? 'Opening checkout…'
                              : `Buy this site${sitePrice ? ` — ${sitePrice}` : ''}`}
                          </span>
                          <span className="block text-[10px] font-normal text-orange-50/80 mt-0.5 leading-tight">
                            One payment · this site only
                          </span>
                        </button>

                        <button
                          onClick={() => navigate('/Pricing')}
                          aria-label={`Subscribe${planPrice ? ` from ${planPrice} a month` : ''} to unlock every site`}
                          className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 text-zinc-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500"
                        >
                          <span className="block text-xs font-semibold leading-tight">
                            {planPrice ? `Subscribe — ${planPrice}/mo` : 'Subscribe monthly'}
                          </span>
                          <span className="block text-[10px] font-normal text-zinc-500 mt-0.5 leading-tight">
                            Every site you build
                          </span>
                        </button>
                      </div>

                      <p className="text-[11px] text-zinc-500 max-w-sm text-center leading-relaxed">
                        Buying once gets you the full code and takes the badge off this site. 
                        <br /> 
                        Subscribing unlocks every site you generate, plus editing, hosting and custom domains.
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-zinc-600">
                <Code2 className="w-10 h-10 opacity-20" />
                <p className="text-sm">No code yet — generate a website first</p>
              </div>
            )}
          </div>
        </div>

        {/* Split preview (Feature 5) */}
        {splitView && (
          <div className="w-1/2 flex flex-col overflow-hidden">
            <div className="h-9 shrink-0 flex items-center gap-2 px-3 border-b border-white/5 bg-zinc-950/80">
              <button className="text-zinc-500 hover:text-white p-1"><ArrowLeft className="w-3.5 h-3.5" /></button>
              <button className="text-zinc-500 hover:text-white p-1"><ChevronRight className="w-3.5 h-3.5" /></button>
              <div className="flex-1 bg-zinc-800 rounded-md px-2 py-0.5 text-xs text-zinc-400">/</div>
              <button className="text-zinc-500 hover:text-white p-1"><RefreshCw className="w-3 h-3" /></button>
              <div className="flex items-center gap-0.5 ml-1">
                {[Monitor, Tablet, Smartphone].map((Icon, i) => (
                  <button key={i} className="p-1 text-zinc-500 hover:text-white">
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
              <button onClick={() => setSplitView(false)} title="Exit split view" className="text-zinc-500 hover:text-white p-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 bg-snow overflow-hidden">
              {websiteCode.html ? (
                <LivePreviewFrame
                  pages={pages}
                  css={websiteCode.css}
                  js={websiteCode.js}
                  icon={websiteCode.icon}
                  isGenerating={isGenerating}
                  title="Split preview"
                  className="w-full h-full border-none"
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-zinc-950">
                  <p className="text-zinc-600 text-sm">Waiting for generation…</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string} [props.prompt] the prompt to build from on mount
 * @param {{base64: string, mimeType: string}|null} [props.initialImage]
 *   an image attached in the home prompt box, sent with that first build
 * @param {Function} [props.onBack]
 * @param {(websiteId: string) => void} [props.onWebsiteCreated]
 *   fired as soon as this run has a saved row, so the route can adopt its id
 * @param {object} [props.existingWebsite]
 * @param {'preview'|'dashboard'|'code'} [props.initialTab]
 * @param {boolean} [props.planFirst] the "Plan first" switch beside Build:
 *   false starts the first build straight away, with no plan to approve
 * @param {string} [props.initialFollowUp] a change written elsewhere, put in
 *   the message box unsent (BuildersLab Business's "Ask your company")
 */
export default function GenerationView({ prompt, initialImage, onBack, onWebsiteCreated, existingWebsite, initialTab, planFirst = true, initialFollowUp = '' }) {
  const { generate, stop, isGenerating, streamedText, websiteId, photos, error, credits, setCredits, planLimit, setPlanLimit, completed, stopped, issues, repairing, rewriting, siteIcon } = useGenerate();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Drives the panel orientation below. Tailwind breakpoints cannot express it:
  // ResizablePanelGroup takes its direction as a prop, not a class.
  const isMobile = useIsMobile();

  // A first build is drawn as a wireframe of the site from the moment it starts
  // (src/lib/wireframe.js). Only a first build: an edit streams over a site the
  // user is already looking at, and a frame drawn over it would be noise.
  // Set by the first-build entry points below and cleared by runRefinement; a
  // retry keeps whatever the run it is retrying was.
  const [firstBuild, setFirstBuild]   = useState(false);

  // One progress reading, shared by the workspace strip, the chat plan and the
  // preview skeleton, so the three can never disagree about how far along it is.
  // Anything but a first build is read as an edit (see readGenerationProgress).
  const progress = useGenerationProgress(
    streamedText, isGenerating, !isGenerating && !!websiteId && !error,
    { edit: !firstBuild, rewriting },
  );

  const [subscriptionTier, setSubscriptionTier]   = useState(null);
  // The rest of the profile — bought credits, form usage, the refresh date —
  // for the credits panel.
  const [profileRow, setProfileRow]               = useState(null);
  const isPaidPlan = ['starter', 'pro', 'agency'].includes(subscriptionTier);
  // Bought outright, for this one site: unlocks the code export and drops the
  // badge here only. Kept separate from isPaidPlan because it grants strictly
  // less — editing and the rest of the plan features stay behind a subscription.
  const [sitePurchased, setSitePurchased] = useState(false);
  const canExportCode = isPaidPlan || sitePurchased;
  const [buyingSite, setBuyingSite] = useState(false);
  const [sitePrice, setSitePrice]   = useState('');
  const [planPrice, setPlanPrice]   = useState('');
  // The badge is on unpaid sites and off on every paid plan — no preference,
  // no toggle. The server applies the same rule when it serves a published
  // page; this only decides what the preview shows. Until the tier has loaded
  // (null), the badge shows — erring toward attribution rather than flashing an
  // unbranded preview at a free user.

  const [deviceView, setDeviceView]               = useState('desktop');
  const [rightTab, setRightTab]                   = useState(initialTab || 'preview');

  const [overrideWebsiteId, setOverrideWebsiteId] = useState(existingWebsite?.id || null);

  // The single source of truth for the preview: LivePreviewFrame diffs these
  // straight into a document that is already loaded, so there is no rendered
  // HTML string to keep in sync alongside them. `pages` is the multi-page site;
  // `html` mirrors the home page for the handful of single-page readers.
  //
  // An opened site is in it from the first render. It used to arrive only once
  // the profile request had come back, and a message sent in that gap had no
  // site to edit — so it went off as a brand-new build, in a new row, for a
  // credit.
  const [websiteCode, setWebsiteCode]             = useState(() => {
    if (!existingWebsite) return { pages: [], html: '', css: '', js: '', title: '', icon: '' };
    const pages = toPages(existingWebsite);
    return {
      pages, html: pages[0]?.html || '',
      css: existingWebsite.css_content || '', js: existingWebsite.js_content || '',
      title: existingWebsite.title || '', icon: existingWebsite.icon_svg || '',
    };
  });
  // Which generated page the preview is showing.
  const [activePage, setActivePage]               = useState(() => (existingWebsite ? homePath(toPages(existingWebsite)) : DEFAULT_PAGE));

  // Memoised so the preview frames receive a stable `pages` identity and don't
  // resubscribe their link handlers on every streamed chunk.
  const pages      = useMemo(() => toPages(websiteCode), [websiteCode]);
  const activeHtml = (pages.find(p => p.path === activePage) ?? pages[0])?.html || '';
  // No prompt means a site opened from the dashboard with nothing typed yet —
  // starting with a blank bubble rendered an empty grey box above the history.
  const [messages, setMessages]                   = useState(
    prompt ? [{ id: 1, role: 'user', content: prompt, timestamp: new Date() }] : [],
  );
  // A change written elsewhere and brought here to send ("Ask your company"
  // in BuildersLab Business). Written into the box, never sent: sending it
  // spends a credit, and that is the owner's call, as with "Fix these".
  const [followUp, setFollowUp]                   = useState(initialFollowUp || '');
  const [attachedFile, setAttachedFile]           = useState(null);
  const [attachedImage, setAttachedImage]         = useState(null);

  const [versionHistory, setVersionHistory]       = useState([]);
  const [isRecording, setIsRecording]             = useState(false);
  const [copySuccess, setCopySuccess]             = useState('');

  // Feature 2: credits panel
  const [showCreditsPanel, setShowCreditsPanel]   = useState(false);
  const [showChatHistory, setShowChatHistory]     = useState(false);
  // The chat column closed, so the preview gets the full width. Collapsed
  // through the panel library rather than unmounted, so the transcript's
  // scroll position and anything half-typed are still there on reopening.
  const [chatCollapsed, setChatCollapsed]         = useState(readChatCollapsed);
  const chatCollapsedRef = useRef(chatCollapsed);
  chatCollapsedRef.current = chatCollapsed;
  /** @type {React.MutableRefObject<import('react-resizable-panels').ImperativePanelHandle | null>} */
  const chatPanelRef = useRef(null);
  // The width it last had open, so reopening restores it rather than the
  // library's minimum.
  /** @type {React.MutableRefObject<number | null>} */
  const chatSizeRef = useRef(null);
  const defaultChatSize = 16;

  // Which pane a phone shows: 'chat' or 'workbench' (the site, its code or the
  // dashboard). A fresh build opens on the chat, where the plan to approve
  // appears; an existing site opens on the site itself.
  const [mobileView, setMobileView] = useState(existingWebsite || !prompt ? 'workbench' : 'chat');
  // A text box has focus, so the keyboard is up: the tab bar gives it the room.
  const [typing, setTyping] = useState(false);

  // No-ops while the panel is unmounted (the Dashboard tab): `chatCollapsed`
  // is what the panel mounts from when it comes back. On a phone "open" means
  // bring the chat on screen — there is no column to widen.
  const openChat = useCallback(() => {
    if (isMobile) {
      setMobileView('chat');
      setRightTab(t => (t === 'dashboard' ? 'preview' : t));
      return;
    }
    const panel = chatPanelRef.current;
    if (panel?.isCollapsed()) panel.resize(chatSizeRef.current || defaultChatSize);
  }, [isMobile, defaultChatSize]);

  const toggleChat = useCallback(() => {
    const panel = chatPanelRef.current;
    if (!panel) return;
    if (panel.isCollapsed()) openChat();
    else panel.collapse();
  }, [openChat]);
  // The message just jumped to from chat history, outlined for a moment.
  const [flashMsgId, setFlashMsgId]               = useState(null);
  // Feature 7: publish panel
  const [showPublishPanel, setShowPublishPanel]   = useState(false);
  const [showGitHub, setShowGitHub]               = useState(false);
  // Whether this site is live. Only used to tell the integrations panel that
  // its settings apply to the published site, so a stale value is harmless.
  // Feature 9: latest messages btn
  const [showLatestBtn, setShowLatestBtn]         = useState(false);
  // Feature 10: editable title
  const [editingTopTitle, setEditingTopTitle]     = useState(false);
  const [topTitleVal, setTopTitleVal]             = useState('');
  const [topTitleHovered, setTopTitleHovered]     = useState(false);

  const chatEndRef        = useRef(null);
  const chatContainerRef  = useRef(null);
  // Whether the transcript is following the bottom. A ref, not state: it is read
  // by the autoscroll on every chunk and must never trigger a render of its own.
  const pinnedRef         = useRef(true);
  const pinRafRef         = useRef(0);
  const textareaRef       = useRef(null);
  const fileInputRef      = useRef(null);
  const recognitionRef    = useRef(null);
  const completionToastedRef = useRef(!!existingWebsite);
  const currentWebsiteIdRef = useRef(existingWebsite?.id || null);
  const topTitleInputRef  = useRef(null);

  // Sync top title with websiteCode.title
  useEffect(() => {
    if (websiteCode.title && !topTitleVal) setTopTitleVal(websiteCode.title);
  }, [websiteCode.title]);

  // Persist a top-bar rename to the API (no-op until the site exists)
  const commitTopTitle = useCallback(async () => {
    setEditingTopTitle(false);
    const next = topTitleVal.trim();
    const id = overrideWebsiteId || currentWebsiteIdRef.current;
    if (!next || !id || next === websiteCode.title) return;
    try {
      await authFetch(`/api/websites/${id}`, { method: 'PATCH', body: JSON.stringify({ title: next }) });
      setWebsiteCode(c => ({ ...c, title: next }));
      toast.success('Renamed!');
    } catch { /* keep local value; API rename is best-effort here */ }
  }, [topTitleVal, overrideWebsiteId, websiteCode.title]);

  const displayTitle = topTitleVal || websiteCode.title || prompt || 'Building…';
  const iconUri      = iconDataUri(websiteCode.icon);

  // An image attached in the home prompt box travels with that very first
  // build; every later refinement carries its own attachment from the chat box.
  const initialBuildOptions = () => (
    initialImage
      ? {
          base64Image: initialImage.base64, imageMimeType: initialImage.mimeType,
          // The same image at tab-icon size, if the browser could draw it — the
          // server uses it as the site's icon when the prompt calls it the logo.
          ...(initialImage.icon ? { iconImage: initialImage.icon } : {}),
        }
      : {}
  );

  useEffect(() => {
    const init = async () => {
      if (user) {
        const { data, error } = await supabase.from('profiles').select('credits, plan_limit, subscription_tier').eq('id', user.id).single();
        if (error || !data) {
          // A failed fetch is NOT the same as an exhausted account. Leaving
          // credits null keeps the counter at '…' instead of rendering a
          // fabricated "0 of 5" plan and a false "out of credits" banner.
          console.error('[CREDITS] profile fetch failed:', error?.message || 'no profile returned');
        } else {
          setCredits(data.credits ?? 0);
          setPlanLimit(data.plan_limit ?? messageCreditsForTier(data.subscription_tier));
          setSubscriptionTier(data.subscription_tier || 'free');
          setProfileRow(data);
        }
        if (existingWebsite) {
          // The site itself is already in websiteCode (its initial state).
          setOverrideWebsiteId(existingWebsite.id);
        } else if (prompt && remaining > 0) {
          startBuild(prompt);
        }
      } else if (prompt && !existingWebsite) {
        startBuild(prompt);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!websiteId) return;
    currentWebsiteIdRef.current = websiteId;
    // Tells the route this run now has a saved record, so /build can become
    // /build/:websiteId while the generation is still streaming.
    onWebsiteCreated?.(websiteId);
  }, [websiteId, onWebsiteCreated]);

  // Closing the workspace mid-dictation would otherwise leave the recogniser
  // running and the browser's mic indicator lit.
  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  const effectiveWebsiteId = overrideWebsiteId || websiteId;

  // ── hand-edited code ──────────────────────────────────────────────────────
  // Drafts live here, not in CodeTab, because that component unmounts on every
  // right-hand tab change. A ref mirrors them so the debounced autosave always
  // reads the latest edit rather than the value captured when the timer was set.
  const [codeDrafts, setCodeDrafts] = useState({});
  const [codeSaveState, setCodeSaveState] = useState({ status: 'idle', message: null });
  const codeDraftsRef = useRef(codeDrafts);
  codeDraftsRef.current = codeDrafts;
  // Same reason: the autosave callback must read the current source, not the
  // snapshot that existed when the debounce timer was scheduled.
  const websiteCodeRef = useRef(websiteCode);
  websiteCodeRef.current = websiteCode;

  const isCodeDirty = Object.keys(codeDrafts).length > 0;
  const canEditCode = isPaidPlan && !isGenerating && !!effectiveWebsiteId;
  // Visual editing is free on every tier: it spends no tokens, and charging for
  // it would push people back to the prompt for changes a click can make.
  // The server enforces the same line — markup-only saves need no plan.
  const canVisualEdit = !isGenerating && !!effectiveWebsiteId;

  // Drop drafts when the site changes identity, otherwise edits from one site
  // would be saved onto the next one opened in the same session.
  useEffect(() => {
    setCodeDrafts({});
    setCodeSaveState({ status: 'idle', message: null });
  }, [effectiveWebsiteId]);

  // Persist hand-edited source, then fold it back into websiteCode so the live
  // preview reflects the save without a refetch. Throws on failure so CodeTab
  // can surface the reason and keep the user's drafts intact.
  const handleSaveCode = useCallback(async ({ pages: nextPages, css_content, js_content, icon_svg }) => {
    const id = effectiveWebsiteId || currentWebsiteIdRef.current;
    if (!id) throw new Error('This site has not been saved yet.');

    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Please sign in again.');

    const res = await fetch(`${apiUrl}/api/websites/${id}/code`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ pages: nextPages, css_content, js_content, icon_svg }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Carry the server's code through. Without it every failure — a lapsed
      // plan, an oversized file, a schema problem — looked identical to the
      // user, and identical in a bug report.
      const detail = data.code && data.code !== 'UNKNOWN' ? ` (${data.code})` : '';
      throw new Error(`${data.error || `Save failed — HTTP ${res.status}`}${detail}`);
    }

    // What the server stored can differ from what was sent: a picture inlined as
    // base64 is moved to /uploads on save (routes/websites.js). Folding back the
    // sent copy would carry the base64 into every save after this one.
    const stored = data.website ? toPages(data.website) : [];
    const savedPages = stored.length ? stored : nextPages;
    const home = savedPages.find(p => p.path === DEFAULT_PAGE) ?? savedPages[0];
    setWebsiteCode(c => ({
      ...c,
      pages: savedPages,
      html:  home?.html ?? c.html,
      css:   css_content !== undefined ? (data.website?.css_content ?? css_content) : c.css,
      js:    js_content ?? c.js,
      ...(icon_svg !== undefined ? { icon: icon_svg } : {}),
    }));

    return data;
  }, [effectiveWebsiteId]);

  // A plan or credit payment returns to the account dashboard, which says so
  // (components/dashboard/AccountDashboard.jsx). The only returns that land in
  // a site's workspace are a site bought outright and a marketplace purchase,
  // handled below. A generic "your plan is active" here used to fire first and
  // strip the flag, so a site purchase was congratulated as a plan upgrade.

  // ── Adopt the saved site once the run ends ────────────────────────────────
  // The preview renders what the stream said; the server saves something a
  // little different. Two parsers implement the delimiter format — parseStream
  // here and parseDelimited on the server — and only the server also sanitises
  // the markup and closes tags the model left open. Photographs are resolved
  // there too. So the preview could finish a generation showing a page that was
  // never quite what got stored, which is what "the live preview looks
  // unfinished but the published link is fine" actually was.
  //
  // Rather than keep the two parsers in step forever, the workspace re-reads
  // the row it just wrote. One request, and from then on the preview, the code
  // tab, the export and the published site are all the same bytes.
  const adoptedRef = useRef('');

  useEffect(() => {
    if (isGenerating || !effectiveWebsiteId) return;
    // Only after a run this session, and only once per finished generation.
    if (!streamedText || adoptedRef.current === effectiveWebsiteId) return;
    adoptedRef.current = effectiveWebsiteId;

    let cancelled = false;
    authFetch(`/api/websites/${effectiveWebsiteId}`)
      .then(({ website }) => {
        if (cancelled || !website) return;
        const pages = toPages(website);
        if (!pages.length) return;
        const home = pages.find(p => p.path === DEFAULT_PAGE) ?? pages[0];
        setWebsiteCode(c => ({
          ...c,
          pages,
          html: home?.html ?? c.html,
          css:  website.css_content ?? c.css,
          js:   website.js_content  ?? c.js,
          title: website.title || c.title,
          icon:  website.icon_svg || c.icon,
        }));
      })
      .catch(() => {});   // the streamed version stays on screen
    return () => { cancelled = true; };
  }, [isGenerating, effectiveWebsiteId, streamedText]);

  // ── Plan mode ─────────────────────────────────────────────────────────────
  // A new build is drafted as a plan first. The plan is free and fast; the
  // build is a credit and a minute. Reviewing and correcting the plan here is
  // what stops a wrong build being paid for twice. Skipped when the "Plan
  // mode" switch beside Build was off (`planFirst`, via router state).
  //
  // planStatus: null (no plan step) | 'drafting' | 'ready' | 'revising' |
  // 'error'. A failure is shown, never papered over: it used to start the
  // build anyway, so with Plan mode on people were charged for a site they
  // never got to review — which is what "plan mode does not work" was.
  const [pendingPlan, setPendingPlan] = useState(null);
  const [planStatus, setPlanStatus]   = useState(null);
  const [planError, setPlanError]     = useState('');
  // Earlier versions of the plan, newest last, for Undo after a revision.
  const [planHistory, setPlanHistory] = useState([]);
  // The plan that was actually approved, kept for the duration of the build so
  // the task list can show every page before the stream reaches it.
  const [approvedPlan, setApprovedPlan] = useState(null);
  const buildPromptRef                = useRef('');
  const pendingPlanRef                = useRef(null);
  pendingPlanRef.current = pendingPlan;

  // ── Build wireframe ───────────────────────────────────────────────────────
  // firstBuild (declared beside the progress reading) decides it.
  // The preview follows the page being written until the user picks one.
  const [followLive, setFollowLive]   = useState(true);

  // The last run as it was sent, for "Try again". Those buttons used to resend
  // the last message's text alone, and an edit without its site is a request
  // for a new site: "make the navbar transparent on scroll" came back as a
  // whole website built from that one sentence, in a new row, for a credit.
  const lastRunRef = useRef(null);

  const beginFirstBuild = useCallback((text, options) => {
    lastRunRef.current = { text, options, firstBuild: true };
    setFirstBuild(true);
    setFollowLive(true);
    generate(text, options);
  }, [generate]);

  const retryLastRun = useCallback(() => {
    const last = lastRunRef.current;
    if (!last || isGenerating) return;
    setFirstBuild(last.firstBuild);
    if (last.firstBuild) setFollowLive(true);
    generate(last.text, last.options);
  }, [generate, isGenerating]);

  /**
   * Draft the plan, or revise the one on screen when `feedback` is given.
   * Free either way. A revision keeps the user's own notes (the server carries
   * them over) and can be undone.
   */
  const draftPlan = useCallback(async ({ feedback = '' } = {}) => {
    const current = pendingPlanRef.current;
    const revising = !!feedback && !!current;
    setPlanStatus(revising ? 'revising' : 'drafting');
    setPlanError('');
    try {
      const { plan } = await authFetch('/api/generate/plan', {
        method: 'POST',
        body: JSON.stringify({
          prompt: buildPromptRef.current,
          ...(revising ? { previousPlan: current, feedback } : {}),
        }),
      });
      if (!plan?.pages?.length) throw new Error('The plan came back empty.');
      if (revising) setPlanHistory(h => [...h, current].slice(-10));
      else setPlanHistory([]);
      setPendingPlan(plan);
      setPlanStatus('ready');
      return plan;
    } catch (err) {
      setPlanError(err.message || 'Could not draft a plan.');
      // A failed revision leaves the plan that was there, with the reason.
      setPlanStatus('error');
      return null;
    }
  }, []);

  const startBuild = useCallback(async (text) => {
    buildPromptRef.current = text;
    // Plan mode was switched off beside Build: no plan call, straight to it.
    if (!planFirst) { beginFirstBuild(text, initialBuildOptions()); return; }
    await draftPlan();
  }, [beginFirstBuild, planFirst, draftPlan]);

  const leavePlanStage = useCallback(() => {
    setPendingPlan(null);
    setPlanStatus(null);
    setPlanError('');
    setPlanHistory([]);
  }, []);

  const buildFromPlan = useCallback((plan) => {
    // Client-only bookkeeping (stable keys, auto file names) stays here.
    const clean = {
      ...plan,
      pages: (plan.pages || []).map(({ uid: _uid, autoPath: _auto, ...p }) => p),
    };
    leavePlanStage();
    setApprovedPlan(clean);
    setMessages(prev => [...prev, {
      id: Date.now(), role: 'ai', timestamp: new Date(),
      content: `Building “${clean.title || 'your site'}” from the plan — ${clean.pages.length} page${clean.pages.length === 1 ? '' : 's'}.`,
    }]);
    beginFirstBuild(buildPromptRef.current, { ...initialBuildOptions(), plan: clean });
  }, [beginFirstBuild, leavePlanStage]);

  const buildWithoutPlan = useCallback(() => {
    leavePlanStage();
    setApprovedPlan(null);
    beginFirstBuild(buildPromptRef.current, initialBuildOptions());
  }, [beginFirstBuild, leavePlanStage]);

  const undoPlan = useCallback(() => {
    if (!planHistory.length) return;
    setPendingPlan(planHistory[planHistory.length - 1]);
    setPlanHistory(planHistory.slice(0, -1));
    setPlanStatus('ready');
    setPlanError('');
  }, [planHistory]);

  // ── Plan mode for changes ─────────────────────────────────────────────────
  // With the Plan switch on in the chat, a message about an existing site is
  // planned first (free): which pages change and how. Apply spends the credit;
  // anything typed before that adjusts the plan instead.
  const [editPlanMode, setEditPlanModeState] = useState(readEditPlanMode);
  const setEditPlanMode = useCallback((on) => {
    setEditPlanModeState(on);
    writeEditPlanMode(on);
  }, []);
  const [editPlan, setEditPlan] = useState(null);   // { request, plan, prompt, status, error }

  // The model's ===OUTLINE=== once it is complete, and the page it is writing.
  // The outline is the first thing in the response, so only the head of the
  // stream is read — re-parsing a whole 200KB site on every chunk to find it
  // would be work for nothing. The margin covers a model that writes its
  // design decisions out as a preamble before it.
  const streamOutline = useMemo(() => {
    if (!firstBuild || !streamedText) return '';
    const { outline, outlineDone } = parseStream(streamedText.slice(0, 12000));
    return outlineDone ? outline : '';
  }, [firstBuild, streamedText]);
  const livePath = progress.currentPage || '';

  // What the frame draws: the outline when the model has written one, the plan
  // until then. Rebuilt only when one of those actually changes, so the frames
  // below see a stable object between chunks.
  const buildStructure = useMemo(
    () => (firstBuild && isGenerating ? resolveStructure(approvedPlan, streamOutline) : null),
    [firstBuild, isGenerating, approvedPlan, streamOutline],
  );
  const wireframe = useMemo(
    () => (buildStructure ? { ...buildStructure, livePath } : null),
    [buildStructure, livePath],
  );
  // Every planned page is a tab — and a place the preview can show — from the
  // first second, each holding whatever has been written of it so far.
  const previewPages = useMemo(
    () => (buildStructure ? withPlannedPages(pages, buildStructure) : pages),
    [pages, buildStructure],
  );

  // A page picked by hand — a tab, or a link inside the preview — ends following.
  const choosePage = useCallback((path) => {
    setFollowLive(false);
    setActivePage(path);
  }, []);

  // Follow the stream from page to page, so what is being written is what is on
  // screen. When the run ends, land on the home page like any visitor would.
  useEffect(() => {
    if (!followLive || !buildStructure || !livePath) return;
    setActivePage((cur) => (cur === livePath ? cur : livePath));
  }, [followLive, buildStructure, livePath]);
  const wasBuildingRef = useRef(false);
  useEffect(() => {
    if (buildStructure) { wasBuildingRef.current = true; return; }
    if (!wasBuildingRef.current) return;
    wasBuildingRef.current = false;
    if (followLive && pages.length) setActivePage(homePath(pages));
  }, [buildStructure, followLive, pages]);

  // How long the last run took, start of the request to the saved site — the
  // repair pass included, since it runs before `complete`. Shown on the
  // success card; cleared the moment another run starts.
  const runStartRef = useRef(0);
  const [lastRunMs, setLastRunMs] = useState(null);
  useEffect(() => {
    if (isGenerating) { runStartRef.current = Date.now(); setLastRunMs(null); return; }
    if (!runStartRef.current) return;
    setLastRunMs(Date.now() - runStartRef.current);
    runStartRef.current = 0;
  }, [isGenerating]);

  // ── Visual editing ────────────────────────────────────────────────────────
  // Edits are applied straight to the preview document and cost nothing: no
  // model call, no credit, no wait. Persisting them reuses handleSaveCode, so a
  // visual edit and a hand-typed source edit take exactly the same path —
  // including the version snapshot and the paid-plan check on the server.
  const previewRef                          = useRef(null);
  const [visualEdit, setVisualEdit]         = useState(false);
  const [selectedEl, setSelectedEl]         = useState(null);
  const [visualDirty, setVisualDirty]       = useState(false);
  const [savingVisual, setSavingVisual]     = useState(false);

  // Leaving the page, switching sites or starting a generation all invalidate a
  // half-finished edit, so the mode closes rather than silently holding stale
  // DOM that the next render would discard anyway.
  useEffect(() => {
    if (isGenerating) { setVisualEdit(false); setSelectedEl(null); setVisualDirty(false); }
  }, [isGenerating]);
  useEffect(() => { setVisualEdit(false); setSelectedEl(null); setVisualDirty(false); }, [effectiveWebsiteId, activePage]);

  useEffect(() => {
    if (!effectiveWebsiteId) return;
    let cancelled = false;
    authFetch(`/api/websites/${effectiveWebsiteId}`)
      .then(({ website }) => {
        if (cancelled) return;
        setSitePurchased(!!website?.purchased_at);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [effectiveWebsiteId]);

  const commitVisualEdits = useCallback(async () => {
    const html = previewRef.current?.serialize();
    if (html == null) return;

    setSavingVisual(true);
    try {
      const current  = websiteCodeRef.current;
      const nextPages = toPages(current).map(p =>
        p.path === activePage ? { path: p.path, html } : p);

      // Deliberately sends `pages` and nothing else. A visual edit never touches
      // the stylesheet or the script, and the server allows a markup-only save
      // on every tier — sending css/js unchanged would trip the paid-plan gate
      // for a free user and block an edit that costs us nothing.
      await handleSaveCode({ pages: nextPages });

      setVisualDirty(false);
      setSelectedEl(null);
      setVisualEdit(false);
      toast.success('Edits saved — no credits used.');
    } catch (err) {
      toast.error(err.message || 'Could not save your edits.');
    } finally {
      setSavingVisual(false);
    }
  }, [activePage, handleSaveCode]);

  const discardVisualEdits = useCallback(() => {
    setVisualEdit(false);
    setSelectedEl(null);
    setVisualDirty(false);
    // Leaving edit mode re-enables the render effect, which morphs the document
    // back to the saved source — so discarding needs no explicit restore.
  }, []);

  // Selecting from the breadcrumb has to go through the same path as a click in
  // the frame, so the outline and contenteditable state stay consistent.
  const selectFromPanel = useCallback((el) => {
    const doc = previewRef.current?.getDocument();
    if (!doc) return;
    setSelectedEl(el ? selectNode(doc, el) : null);
    if (!el) previewRef.current?.clearSelection();
  }, []);

  // Rebuild the whole site from generated source plus drafts, so every save
  // carries all edited files, not just the one currently on screen.
  const saveCodeDrafts = useCallback(async () => {
    const drafts = codeDraftsRef.current;
    if (!Object.keys(drafts).length) return;

    setCodeSaveState({ status: 'saving', message: null });
    try {
      const current = websiteCodeRef.current;
      const next = withDrafts(current, drafts);

      await handleSaveCode({
        pages: next.pages,
        css_content: next.css,
        js_content:  next.js,
        ...(current.icon ? { icon_svg: next.icon } : {}),
      });

      // Clear only what was actually sent. Anything typed while the request was
      // in flight stays dirty instead of being silently dropped.
      setCodeDrafts(cur => {
        const next = { ...cur };
        for (const [k, v] of Object.entries(drafts)) {
          if (next[k] === v) delete next[k];
        }
        return next;
      });
      setCodeSaveState({ status: 'saved', message: null });
    } catch (err) {
      setCodeSaveState({ status: 'error', message: err.message || 'Save failed' });
    }
  }, [handleSaveCode]);

  // Autosave: one save per quiet moment rather than per keystroke. The server
  // coalesces version snapshots, so this does not flood the history.
  useEffect(() => {
    if (!isCodeDirty || !canEditCode) return;
    const timer = setTimeout(saveCodeDrafts, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [codeDrafts, isCodeDirty, canEditCode, saveCodeDrafts]);

  // Last line of defence: a refresh or back-navigation inside the autosave
  // window would otherwise take unsaved edits with it.
  useEffect(() => {
    if (!isCodeDirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isCodeDirty]);

  // Reopening a site used to show a one-line transcript: `messages` starts from
  // the `prompt` prop, which is websites.prompt — only ever the MOST RECENT
  // instruction, because /generate overwrites it on every run. Everything asked
  // before that was still on disk (website_versions.prompt, one row per run) but
  // nothing read it, so there was nothing to scroll back to.
  //
  // Seeded once per site: the versions endpoint is re-polled after every
  // generation, and re-seeding would stack the history on each pass.
  const seededHistoryFor = useRef(null);
  const seedTranscriptFromHistory = useCallback((versions) => {
    const id = currentWebsiteIdRef.current;
    if (!id || seededHistoryFor.current === id) return;
    seededHistoryFor.current = id;

    const earlier = [...versions]
      .sort((a, b) => (a.version || 0) - (b.version || 0))
      .map(v => (v.prompt || '').trim())
      .filter(Boolean);
    if (!earlier.length) return;

    setMessages(prev => {
      // The newest version's prompt is usually the same one already sitting in
      // the transcript from the `prompt` prop — don't show it twice.
      const firstExisting = prev.find(m => m.role === 'user')?.content?.trim();
      const history = earlier.filter((p, i) => p !== earlier[i - 1]);
      while (history.length && history[history.length - 1] === firstExisting) history.pop();
      if (!history.length) return prev;

      return [
        ...history.map((content, i) => ({
          id: `history-${id}-${i}`,
          role: 'user',
          content,
          timestamp: null,      // real send time is unknown; the bubble hides it
          historical: true,
        })),
        ...prev,
      ];
    });
  }, []);

  useEffect(() => {
    const id = currentWebsiteIdRef.current;
    if (!id || isGenerating) return;
    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${apiUrl}/api/websites/${id}/versions`, { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data?.versions) return;
          setVersionHistory(data.versions);
          seedTranscriptFromHistory(data.versions);
        })
        .catch(() => {});
    });
  }, [websiteId, isGenerating, seedTranscriptFromHistory]);

  // Feature 9: scroll listener for "Latest messages" button.
  //
  // `pinnedRef` rather than the `showLatestBtn` state is what the autoscroll
  // below reads. A scroll event fires for every frame of every scroll, and
  // driving the pin off state meant a re-render could land between a chunk and
  // its scroll — the thing being followed moved while the follow was deciding.
  // The state is still set, because the button renders from it, but React
  // bails out when the boolean has not actually changed.
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // A pin write lands exactly at the bottom, so it can never flip this on;
      // only the user scrolling away does.
      pinnedRef.current = distFromBottom <= 120;
      setShowLatestBtn(distFromBottom > 120);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Keep the transcript pinned to the bottom while it grows.
  //
  // This used to call scrollIntoView({ behavior: 'smooth' }) on every change of
  // `streamedText` — which is every SSE chunk, and a nine-page site streams over
  // six thousand of them. Smooth scrolling is an *animation*: each call cancels
  // the one still in flight and starts a new one toward a bottom that has moved
  // again, so the pane spent the whole generation re-targeting itself and
  // visibly juddered. Smooth behaviour is for discrete jumps; a continuous
  // append wants a pin.
  //
  // So during a run: one scrollTop write per animation frame, coalescing
  // however many chunks arrived in that frame. The content grows a few pixels
  // per frame and the pin follows it exactly, which reads as smooth precisely
  // because there is no animation to interrupt.
  useEffect(() => {
    if (!pinnedRef.current) return;
    const el = chatContainerRef.current;
    if (!el) return;

    // Not streaming: a message appearing is a discrete jump, and animating it
    // is the right call. There is no second one along in 16ms to cancel it.
    if (!isGenerating) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    cancelAnimationFrame(pinRafRef.current);
    pinRafRef.current = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(pinRafRef.current);
  }, [streamedText, messages, isGenerating]);

  // Chat history → the message in the transcript. The pin is released first:
  // mid-build the next chunk would otherwise snap the pane straight back to the
  // bottom before the scroll listener had seen the jump. Measured against the
  // container rather than via scrollIntoView, which also scrolls every
  // overflow-hidden ancestor it passes and can shift the whole workspace.
  const jumpToMessage = useCallback((id) => {
    setShowChatHistory(false);
    const box = chatContainerRef.current;
    const el  = box?.querySelector(`[data-msg-id="${id}"]`);
    if (!el) return;
    pinnedRef.current = false;
    const offset = el.getBoundingClientRect().top - box.getBoundingClientRect().top;
    box.scrollTo({ top: box.scrollTop + offset - 12, behavior: 'smooth' });
    setFlashMsgId(id);
  }, []);

  useEffect(() => {
    if (flashMsgId === null) return;
    const timer = setTimeout(() => setFlashMsgId(null), 1600);
    return () => clearTimeout(timer);
  }, [flashMsgId]);

  // Every chunk updates this, and LivePreviewFrame diffs it into the document
  // that is already on screen — the preview follows the stream in real time
  // without the frame ever reloading.
  useEffect(() => {
    if (!streamedText) return;
    const parsed = parseStream(streamedText);
    const { css, js, title, icon } = parsed;

    // The generator emits the stylesheet before any markup, so it is applied
    // the moment it arrives rather than waiting for a page to hang it on.
    // Nothing is visible yet, but the Google Fonts @import at the top of it is
    // already downloading — so the first heading paints in the right typeface
    // instead of swapping out of a fallback a second later.
    const hasMarkup = parsed.html.length >= 80 && parsed.html.includes('<');
    if (!hasMarkup) {
      if (css) setWebsiteCode(c => (c.css === css ? c : { ...c, css, title: title || c.title }));
      return;
    }

    // Photographs resolve on their own events while the markup is still being
    // written, so the map is applied here rather than in an effect of its own:
    // this runs on every chunk and rebuilds pages from the raw stream, which
    // would wipe out a src applied anywhere else.
    const pages = applyPhotoMapToPages(parsed.pages, photos);
    // The script gets the same treatment for a different reason: a data-driven
    // page keeps its photographs in a JS array, so they never pass through the
    // markup and applyPhotoMapToPages cannot reach them.
    const resolvedJs = applyPhotoTokens(js, photos);
    const home = pages.find(p => p.path === DEFAULT_PAGE) ?? pages[0];
    // The owner's own logo, when they gave one, outranks the icon the model draws.
    setWebsiteCode({ pages, html: home?.html ?? parsed.html, css, js: resolvedJs, title, icon: siteIcon || icon });
  }, [streamedText, photos, siteIcon]);

  // The owner's logo, the moment the server confirms it is the site's icon —
  // before the stream has any text for the effect above to run on.
  useEffect(() => {
    if (siteIcon) setWebsiteCode(c => (c.icon === siteIcon ? c : { ...c, icon: siteIcon }));
  }, [siteIcon]);

  // A refinement can drop the page the user was on. Fall back to the home page
  // rather than leaving the preview pointed at something that no longer exists.
  // During a first build a planned page counts as existing: it is on screen as
  // its wireframe before a line of it is written.
  useEffect(() => {
    if (!previewPages?.length) return;
    if (!previewPages.some(p => p.path === activePage)) setActivePage(homePath(previewPages));
  }, [previewPages, activePage]);

  useEffect(() => {
    if (!isGenerating && websiteId && !completionToastedRef.current) {
      completionToastedRef.current = true;
      toast.success('Your website is ready! 🎉', { action: { label: 'View it →', onClick: () => setRightTab('dashboard') }, duration: 6000 });
    }
  }, [isGenerating, websiteId, navigate]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, [followUp]);

  const handleCopy = useCallback(async (label, content) => {
    // `content` is the exact file the user is looking at; 'full' is the
    // standalone single-document form of the current page.
    const text = label === 'full' ? buildStandaloneDoc({ ...websiteCode, html: activeHtml }) : (content || '');
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      trackCodeExported({ type: `copy_${label}` });
      toast.success(`${label} copied!`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch { toast.error('Failed to copy to clipboard.'); }
  }, [websiteCode, activeHtml]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${import.meta.env.VITE_API_URL || ''}/api/billing/pricing`, { credentials: 'omit' })
      .then(r => r.ok ? r.json() : null)
      .then((list) => {
        if (cancelled || !list) return;
        const money = (n) => `${list.symbol}${Number(n).toLocaleString(list.locale || 'en-US')}`;
        if (list.perSite) setSitePrice(money(list.perSite));
        // The cheapest paid tier, so the subscription option can quote a real
        // "from" figure rather than sending people to the pricing page blind.
        const starter = list.plans?.starter?.monthly;
        if (starter) setPlanPrice(money(starter));
      })
      .catch(() => {});   // the button still works, it just shows no price
    return () => { cancelled = true; };
  }, []);

  // Back from the gateway. The redirect is the only feedback the buyer gets, so
  // it has to say plainly whether the money bought anything.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get('success');
    const err = params.get('error');
    const pending = params.get('pending');
    if (!['site_purchased', 'marketplace'].includes(ok) && !pending && !['amount_mismatch', 'site_not_found'].includes(err)) return;

    if (ok === 'site_purchased') {
      setSitePurchased(true);
      toast.success('This site is yours — the code is unlocked and the badge is off.');
    } else if (ok === 'marketplace') {
      // A site bought on the marketplace lands here, in the buyer's own copy.
      setSitePurchased(true);
      toast.success('Your new site is ready — edit it, publish it, or download the code.', { duration: 7000 });
    } else if (pending) {
      // A bank transfer settles after the buyer leaves the payment page, so the
      // money is on its way but has not landed yet. The webhook unlocks the site.
      toast.info('Your transfer is being confirmed — this site unlocks as soon as it lands.');
    } else if (err === 'amount_mismatch') {
      toast.error('The payment did not match the price. Nothing was charged to your site — contact support.');
    } else {
      toast.error('We could not find that site. If you were charged, contact support.');
    }
    // Strip the flag so a refresh does not replay the message.
    params.delete('success'); params.delete('error'); params.delete('pending');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  const handleBuySite = useCallback(async () => {
    const id = currentWebsiteIdRef.current || effectiveWebsiteId;
    if (!id) { toast.error('Generate a website first.'); return; }
    setBuyingSite(true);
    try {
      const { link } = await authFetch('/api/billing/flutterwave/initialize-site', {
        method: 'POST',
        body: JSON.stringify({ websiteId: id }),
      });
      if (!link) throw new Error('No checkout link came back.');
      window.location.href = link;
    } catch (e) {
      toast.error(e.message || 'Could not open checkout.');
      setBuyingSite(false);
    }
  }, [effectiveWebsiteId]);

  // The two ways to unlock the code, for the download menus. Same actions and
  // prices as the Code tab's locked banner.
  const unlock = useMemo(() => ({
    sitePrice, planPrice, buying: buyingSite,
    onBuySite: handleBuySite,
    onSubscribe: () => navigate('/Pricing'),
  }), [sitePrice, planPrice, buyingSite, handleBuySite, navigate]);

  /**
   * @param {'html'|'react'} [format] 'react' converts the site into a Vite
   *        project; 'html' ships the files the site actually runs as.
   */
  const handleDownloadZip = useCallback(async (format = 'html') => {
    if (!canExportCode) {
      toast.error('Buy this site or upgrade your plan to download the code.');
      navigate('/Pricing');
      return;
    }
    const react = format === 'react';
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      // What the Code tab shows, including edits autosave has not sent yet.
      const site = withDrafts(websiteCode, codeDrafts);
      // Every generated page plus the shared assets, so the internal links in
      // the export resolve against real files on disk.
      const files = react ? buildReactFiles(site) : buildSiteFiles(site);
      files.forEach(f => zip.file(f.name, f.content));
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = siteSlug(websiteCode.title);
      a.href = url; a.download = react ? `${slug}-react.zip` : `${slug}.zip`; a.click();
      URL.revokeObjectURL(url);
      trackCodeExported({ type: react ? 'react' : 'zip', files: files.length });
      toast.success(react
        ? `React project downloaded — ${files.length} files. Run npm install, then npm run dev.`
        : `Website downloaded — ${files.length} files. Open index.html, or upload the folder to any web host.`);
    } catch (err) { toast.error('Failed to create ZIP: ' + err.message); }
  }, [websiteCode, codeDrafts, canExportCode, navigate]);

  // A link the model wrote to a page it never generated. Rather than let it
  // navigate out of the preview, say so and offer to build the missing page.
  const handleBlockedLink = useCallback((href) => {
    toast.warning(`"${href}" isn't a page yet.`, {
      description: 'That link points to a page this site does not have.',
      action: {
        label: 'Build it',
        // The request lands in the message box, which is in the chat.
        onClick: () => { setFollowUp(`Add a ${href} page and link it from the nav on every page.`); openChat(); },
      },
      duration: 6000,
    });
  }, [openChat]);

  const handleRevert = useCallback((version) => {
    const pages = toPages(version);
    const css = version.css_content || '', js = version.js_content || '';
    setWebsiteCode({ pages, html: pages[0]?.html || '', css, js, title: websiteCode.title, icon: websiteCode.icon });
    setActivePage(homePath(pages));
    toast.success(`Reverted to version ${version.version}`);
  }, [websiteCode.title, websiteCode.icon]);

  // Extracted so a one-click action can run a refinement without going through
  // the chat box — the "add order buttons" fix needs to submit a prompt the user
  // never typed, and duplicating this logic is how the two drift apart.
  //
  // `echo: false` runs it without adding the text as a new message — an
  // approved edit plan is applied with a long instruction the user did not
  // type, and their own request is already in the transcript above it.
  const runRefinement = useCallback((text, { echo = true } = {}) => {
    if (!text || isGenerating) return;
    if (credits !== null && credits <= 0) { toast.error('No credits remaining. Upgrade to keep building.'); return; }
    // Archive the previous turn as a short summary. It used to push the entire
    // raw generator output into the transcript, which dumped a wall of HTML/JS
    // into the chat — the code belongs in the Code tab, not the conversation.
    if (streamedText) {
      const { title: builtTitle } = parseStream(streamedText);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'ai',
        content: builtTitle ? `Built “${builtTitle}”. Opened it in the preview.` : 'Website updated. Opened it in the preview.',
        timestamp: new Date(),
      }]);
    }
    if (echo) setMessages(prev => [...prev, { id: Date.now() + 1, role: 'user', content: text, timestamp: new Date() }]);
    completionToastedRef.current = false;
    setFollowUp('');
    // When the site is already saved, send only its id — the server reads the
    // current HTML/CSS/JS from the database. POSTing the generated markup back
    // trips the host WAF, which rejects it with a bare "403 Forbidden".
    const savedId = currentWebsiteIdRef.current;
    const ctx = {
      ...(websiteCode.html
        ? (savedId
            ? { existingWebsiteId: savedId }
            : { previousHtml: websiteCode.html, previousCss: websiteCode.css, previousJs: websiteCode.js })
        : {}),
      ...(attachedImage ? {
        base64Image: attachedImage.base64, imageMimeType: attachedImage.mimeType,
        ...(attachedImage.icon ? { iconImage: attachedImage.icon } : {}),
      } : {}),
    };
    setAttachedFile(null); setAttachedImage(null);
    // An edit streams over the site already on screen — never a wireframe.
    lastRunRef.current = { text, options: ctx, firstBuild: false };
    setFirstBuild(false);
    generate(text, ctx);
  }, [isGenerating, streamedText, generate, websiteCode, attachedImage, credits]);

  /** Plan a change to the open site, or revise the plan already in the chat. */
  const planChange = useCallback(async (text, { echo = true, fresh = false } = {}) => {
    const id = currentWebsiteIdRef.current || overrideWebsiteId;
    if (!id) { runRefinement(text); return; }
    const current = fresh ? null : editPlan;
    const revising = !!(current?.plan && current.status !== 'drafting');
    const request = revising ? current.request : text;

    if (echo) setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text, timestamp: new Date() }]);
    setFollowUp('');
    setEditPlan({ request, plan: revising ? current.plan : null, prompt: current?.prompt, status: revising ? 'revising' : 'drafting', error: '' });
    try {
      const res = await authFetch('/api/generate/plan-edit', {
        method: 'POST',
        body: JSON.stringify({
          websiteId: id,
          request,
          ...(revising ? { previousPlan: current.plan, feedback: text } : {}),
        }),
      });
      setEditPlan({ request, plan: res.plan, prompt: res.prompt, status: 'ready', error: '' });
    } catch (err) {
      setEditPlan(cur => ({ ...(cur || { request }), status: 'error', error: err.message || 'Could not plan that change.' }));
    }
  }, [editPlan, overrideWebsiteId, runRefinement]);

  const applyEditPlan = useCallback(() => {
    if (!editPlan?.prompt) return;
    const { prompt: approved } = editPlan;
    setEditPlan(null);
    runRefinement(approved, { echo: false });
  }, [editPlan, runRefinement]);

  const discardEditPlan = useCallback(() => {
    setEditPlan(null);
    setMessages(prev => [...prev, { id: Date.now(), role: 'ai', content: 'Discarded that plan — nothing was changed.', timestamp: new Date() }]);
  }, []);

  const handleFollowUp = useCallback(() => {
    const text = followUp.trim();
    if (!text) return;

    // A new site waiting on its plan: what is typed changes the plan, free.
    if (planStatus && !isGenerating) {
      setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text, timestamp: new Date() }]);
      setFollowUp('');
      if (pendingPlanRef.current) {
        draftPlan({ feedback: text }).then((plan) => {
          if (plan) setMessages(prev => [...prev, { id: Date.now() + 1, role: 'ai', content: 'Updated the plan — have a look.', timestamp: new Date() }]);
        });
      } else {
        // No plan yet (it failed): treat it as more detail for the request.
        buildPromptRef.current = `${buildPromptRef.current}\n\n${text}`.trim();
        draftPlan();
      }
      return;
    }

    // Plan mode for edits: plan first while the switch is on, or while a
    // planned change is waiting (what is typed then adjusts it).
    const hasSite = !!(currentWebsiteIdRef.current || overrideWebsiteId) && !!websiteCode.html;
    if (hasSite && (editPlanMode || (editPlan && editPlan.status !== 'drafting'))) {
      if (credits !== null && credits <= 0) { toast.error('No credits remaining. Upgrade to keep building.'); return; }
      planChange(text);
      return;
    }
    runRefinement(text);
  }, [followUp, planStatus, isGenerating, draftPlan, overrideWebsiteId, websiteCode.html, editPlanMode, editPlan, credits, planChange, runRefinement]);

  const handleStop = useCallback(() => {
    stop();
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'ai',
      content: 'Stopped. What was built so far is saved — carry on from here, or ask for something else.',
      timestamp: new Date(),
    }]);
    toast('Generation stopped.');
  }, [stop]);

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFollowUp(); } };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Cleared first so picking the same file twice in a row still fires.
    e.target.value = '';
    if (!file) return;

    try {
      const { file: picked, image } = await readImageAttachment(file);
      setAttachedFile(picked);
      setAttachedImage(image);
    } catch (err) {
      // Previously a non-image was kept in `attachedFile` and shown as an
      // accepted chip, then silently dropped at send time — only `attachedImage`
      // is ever transmitted. Refusing it here is the honest outcome.
      setAttachedFile(null);
      setAttachedImage(null);
      toast.error(err.message);
    }
  };

  const handleVoice = () => {
    if (isRecording) { recognitionRef.current?.stop(); return; }
    if (!speechRecognitionAvailable()) {
      toast.error('Voice input is not supported in this browser.');
      return;
    }
    recognitionRef.current = startDictation({
      onTranscript: (transcript) => setFollowUp(prev => appendTranscript(prev, transcript).slice(0, MAX_PROMPT)),
      onEnd: () => setIsRecording(false),
      onError: (event) => {
        setIsRecording(false);
        const message = dictationErrorMessage(event);
        if (message) toast.error(message);
      },
    });
    setIsRecording(true);
  };

  const userInitial = (user?.user_metadata?.full_name || user?.email || 'U')[0].toUpperCase();
  const total       = planLimit ?? messageCreditsForTier(subscriptionTier);
  const remaining   = credits ?? total;
  const pct         = remaining / total;
  const textColor   = pct > 0.4 ? 'text-orange-400' : pct > 0 ? 'text-yellow-400' : 'text-red-400';

  const RIGHT_TABS = [{ key: 'preview', label: 'Preview' }, { key: 'dashboard', label: 'Dashboard' }, { key: 'code', label: 'Code' }];

  // Preview · Dashboard · Code are about a SITE, so they show only once there
  // is one — being built, or opened. They used to be on screen everywhere,
  // including the account dashboard and a plan that had not been built yet,
  // where Preview had nothing to preview and Code had no code.
  const siteInView = !!(effectiveWebsiteId || isGenerating || streamedText || existingWebsite);
  // A new site still at its plan: the plan fills the workbench until Build.
  const inPlanStage = !!planStatus && !isGenerating && !siteInView;

  // The generator stream belongs to Preview and Code — the two views that are
  // about the site being built. The site's Dashboard gets the full width.
  //
  // Safe to unmount mid-build: the generation lives in useGenerate() up here in
  // the parent, not in the panel, so hiding the panel cannot interrupt a stream
  // or lose what has already arrived.
  const showChat = rightTab !== 'dashboard' || !siteInView;

  // Things only the chat shows, and that wait on the user: a plan to review,
  // a planned change to apply, what a finished build could not fix, an error,
  // a run that ended unsaved, running out of credits. Behind a closed chat the
  // workspace would just sit there looking finished. It opens on the change
  // only, so closing it again over one of them is respected.
  const chatNeedsYou = !isGenerating && (
    (!!pendingPlan && isMobile)
    || planStatus === 'error'
    || editPlan?.status === 'ready'
    || !!error
    || (completed && issues?.length > 0)
    || credits === 0
    || (!completed && !stopped && !!streamedText)
  );
  useEffect(() => {
    if (!chatNeedsYou) return;
    // On a phone the chat is a whole screen away whether or not a desktop
    // column was closed, so it comes forward every time.
    if (isMobile || chatCollapsedRef.current) openChat();
  }, [chatNeedsYou, openChat, isMobile]);

  // A build that starts on a phone is watched on the site, not in the chat:
  // the preview is the thing being built, and the floating bar keeps the
  // progress and the Stop button one tap away.
  useEffect(() => {
    if (isMobile && isGenerating) setMobileView('workbench');
  }, [isMobile, isGenerating]);

  // ── Layout: resizable columns on a desktop, one pane at a time on a phone ──
  const Group    = isMobile ? PhoneStack : ResizablePanelGroup;
  const ChatPane = isMobile ? PhonePane  : ResizablePanel;
  const WorkPane = isMobile ? PhonePane  : ResizablePanel;
  const groupProps = isMobile ? {} : { direction: 'horizontal', className: 'flex-1 overflow-hidden' };
  const chatPaneProps = isMobile
    ? { active: mobileView === 'chat', label: 'Chat', className: 'bg-zinc-950' }
    : {
        id: 'chat',
        order: 1,
        // The stream is a commentary column, not the work. At 20% a 1366px
        // laptop gave the site ~1090px — narrow enough that a desktop layout
        // was being judged at something closer to tablet width. 16% leaves it
        // ~1140px there and ~1610px at 1920, and the chat's cards wrap rather
        // than truncate at the ~220px this leaves it. Still draggable either
        // way, and the chat toggle hides it outright.
        //
        // Collapsible to nothing, which keeps it mounted — dragging the
        // divider most of the way to the edge closes it too. A closed chat
        // mounts closed (defaultSize 0 is valid for a collapsible panel), so
        // a reload does not flash it open first.
        ref: chatPanelRef,
        collapsible: true,
        collapsedSize: 0,
        defaultSize: chatCollapsed ? 0 : defaultChatSize,
        minSize: 12,
        onCollapse: () => { setChatCollapsed(true); writeChatCollapsed(true); },
        onExpand: () => { setChatCollapsed(false); writeChatCollapsed(false); },
        onResize: (size) => { if (size > 0) chatSizeRef.current = size; },
        className: `flex flex-col bg-zinc-950 overflow-hidden relative ${chatCollapsed ? '' : 'border-r border-white/5'}`,
      };
  const workPaneProps = isMobile
    ? { active: !showChat || mobileView === 'workbench', label: 'Site', className: 'bg-zinc-950' }
    : {
        id: 'workbench',
        order: 2,
        // The two defaults sum to 100. They used to be 20 + 74, which left
        // the library to reconcile a layout that did not add up.
        defaultSize: !showChat || chatCollapsed ? 100 : 100 - defaultChatSize,
        minSize: 50,
        className: 'flex flex-col bg-zinc-950 overflow-hidden relative',
      };

  // The phone's tab bar: the chat, then the three views the desktop has as
  // tabs across the top.
  const phoneTabs = [
    // The dot says the chat has something while the site is on screen.
    { key: 'chat',      label: 'Chat',      icon: MessageSquare, dot: mobileView !== 'chat' && (!!editPlan || planStatus === 'error') },
    { key: 'preview',   label: 'Preview',   icon: Eye },
    { key: 'code',      label: 'Code',      icon: Code2 },
    { key: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
  ];
  // The document behind the workspace does not scroll while it is open (see
  // .bl-app-shell in index.css); the panes scroll themselves.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('bl-app-shell');
    return () => html.classList.remove('bl-app-shell');
  }, []);

  // Nothing to switch between until there is a site: during the plan, Preview,
  // Code and Dashboard would each be an empty screen. The plan is in the chat.
  const hasWork = siteInView;
  const selectPhoneTab = (key) => {
    if (key === 'chat') { setRightTab(t => (t === 'dashboard' ? 'preview' : t)); setMobileView('chat'); return; }
    setRightTab(key);
    setMobileView('workbench');
  };

  // A closed chat is still in the DOM, so keep it out of the tab order and
  // away from screen readers until it is opened again.
  useEffect(() => {
    const el = showChat ? getPanelElement('chat') : null;
    if (el) el.inert = chatCollapsed;
  }, [chatCollapsed, showChat]);

  // A phone shows the plan in the chat pane, so that is where it opens.
  useEffect(() => {
    if (isMobile && inPlanStage) setMobileView('chat');
  }, [isMobile, inPlanStage]);

  // The device switcher is hidden on a phone, so a `deviceView` left on tablet
  // or mobile from a desktop session would otherwise strand the user inside a
  // bezel mockup with no visible control to leave it. The phone is already the
  // mobile rendering; let the preview fill the band.
  const effectiveDeviceView = isMobile ? 'desktop' : deviceView;

  // A page, not an overlay. This was `fixed inset-0 z-[100]`, which floated the
  // workspace above a still-mounted home page — the URL never changed, Back
  // closed the whole thing, and a refresh landed on marketing. It now owns its
  // route (/build, see pages/Workspace.jsx), with nothing mounted beneath it.
  //
  // Fixed to the screen again, but for a different reason: it is the route's
  // own shell, pinned so the document behind it can never carry it off. In the
  // page flow at h-[100dvh], iOS Safari scrolled the whole app up by an
  // address bar's height — body is min-h-screen, and its 100vh is taller than
  // the visible screen while the bar shows — and the tab bar went with it.
  // See .bl-app-shell in index.css, set below while this is mounted. (dvh was
  // there because vh counts the collapsing address bar; inset-0 has neither
  // problem, as it is the viewport itself.)
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-zinc-950 flex flex-col overflow-hidden overscroll-none"
      // Typing on a phone: the keyboard takes ~40% of the screen, and the tab
      // bar's 56px is better spent on the message being written.
      onFocusCapture={(e) => { if (isMobile && isTextEntry(e.target)) setTyping(true); }}
      onBlurCapture={(e) => { if (isTextEntry(e.target)) setTyping(false); }}
    >

      {/* ══ GLOBAL TOP BAR ═══════════════════════════════════════════════════ */}
      {/* Wraps on mobile: branding and actions share the first row, the tab
          switcher takes the full width of a second one. Unwrapped from `md` up,
          where the original three-column layout fits. */}
      <div className="min-h-14 border-b border-zinc-800 bg-zinc-950/85 backdrop-blur flex items-center flex-wrap md:flex-nowrap px-3 md:px-4 gap-2 md:gap-3 shrink-0 py-1.5">

        {/* Left: always the site — the back arrow (to the account dashboard)
            and the site's name. The workspace is only ever about one site now;
            the account dashboard has a console header of its own. */}
        <div className="flex items-center gap-2 flex-1 md:flex-none md:w-[300px] shrink min-w-0 order-1">
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to the dashboard" className="text-zinc-400 hover:text-white rounded-full h-8 w-8 shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {/* The site's own mark once the generator has drawn it; the orange
              BuildersLab badge stands in until then. */}
          {iconUri ? (
            <img
              src={iconUri}
              alt={`${displayTitle} icon`}
              className="w-7 h-7 rounded-lg shrink-0 bg-zinc-900 ring-1 ring-white/10 object-contain"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-ink text-xs font-bold shrink-0">
              B
            </div>
          )}
          <div
            className="min-w-0 flex-1"
            onMouseEnter={() => setTopTitleHovered(true)}
            onMouseLeave={() => setTopTitleHovered(false)}
          >
            <div className="flex items-center gap-1 group">
              {editingTopTitle ? (
                <input
                  ref={topTitleInputRef}
                  autoFocus
                  value={topTitleVal}
                  onChange={e => setTopTitleVal(e.target.value)}
                  onBlur={commitTopTitle}
                  onKeyDown={e => { if (e.key === 'Enter') commitTopTitle(); }}
                  className="text-white font-medium text-sm bg-transparent border-b border-orange-500 outline-none w-full min-w-0"
                />
              ) : (
                <>
                  {/* Wraps to a second line rather than truncating, so long
                      titles read in full instead of "LuxeHaven | Premium…". */}
                  <p className="text-white font-medium text-sm flex items-start gap-1 leading-snug min-w-0" title={displayTitle}>
                    
                    <span className="line-clamp-1 md:line-clamp-2 break-words">{displayTitle}</span>
                  </p>
                  {topTitleHovered && (
                    <button onClick={() => setEditingTopTitle(true)} className="text-zinc-500 hover:text-white shrink-0">
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>
            <p className="hidden md:block text-[11px] text-zinc-500 truncate leading-none mt-0.5">
              {inPlanStage ? 'Planning — nothing built yet' : 'Your Workspace'}
            </p>
          </div>
        </div>

        {/* Center: Preview / Dashboard / Code tabs — only once there is a site
            to preview, manage and read the code of. Before that, while a new
            site is at its plan, the centre says so instead. Not on a phone:
            they are the bottom tab bar there, beside Chat. */}
        <div className="hidden md:flex order-3 md:order-2 w-full md:w-auto md:flex-1 items-center justify-center">
          {siteInView ? (
            <div className="flex items-center w-full md:w-auto bg-zinc-900 border border-zinc-800 rounded-full p-1 gap-0.5">
              {RIGHT_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  aria-current={rightTab === key ? 'page' : undefined}
                  onClick={() => setRightTab(key)}
                  className={`flex-1 md:flex-none px-3 md:px-4 py-1.5 text-sm rounded-full font-semibold transition-all ${
                    rightTab === key ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : planStatus ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/25 bg-orange-500/10 px-3.5 py-1.5 text-xs font-semibold text-orange-300">
              <ListChecks className="w-3.5 h-3.5" /> Plan mode
              <span className="font-normal text-orange-200/70">· free until you build</span>
            </span>
          ) : null}
        </div>

        {/* Right: credits + upgrade + publish */}
        <div className="flex items-center gap-1.5 md:gap-2 order-2 md:order-3 md:w-[276px] justify-end shrink-0">
          <ThemeToggle size="sm" className="hidden md:inline-flex" />
          {/* Credits — click opens credits panel (Feature 2).
              This used to render one dot per credit. At 250 credits that is
              ~2,250px of dots inside a 240px shrink-0 container, so the strip
              overflowed across the header and collided with the site title and
              the centre tabs. The count says the same thing in a fixed width. */}
          <button
            // The credits panel slides over the chat, so a closed chat opens.
            onClick={() => { setShowCreditsPanel(true); openChat(); }}
            title={credits !== null ? `${remaining} of ${total} credits left` : undefined}
            aria-label={credits !== null ? `Credits: ${remaining} of ${total} left` : 'Credits'}
            className="flex items-center bg-zinc-900 border border-zinc-800 rounded-full px-2.5 md:px-3 py-1 hover:border-zinc-700 transition-colors"
          >
            {/* What is LEFT, as the sidebar and the credits panel say it. This
                read `${used}/${total}` — "3/20" on an account with 17 credits
                to spend reads as three left, and on a free plan that looks
                like the account is nearly out. */}
            {/* "credits" spelled out: a bare "19 left" did not say of what. */}
            <span className={`text-[11px] font-semibold tabular-nums whitespace-nowrap ${textColor}`}>{credits !== null ? `${remaining} ${remaining === 1 ? 'credit' : 'credits'} left` : '…'}</span>
          </button>
          {/* Upgrade is reachable from the credits panel this sits next to, so
              it yields its space on mobile rather than crowding Publish. Pro
              and Studio, who cannot upgrade much further, are offered extra
              credits instead. */}
          {canBuyCreditPacks(subscriptionTier) ? (
            <Button size="sm" onClick={() => navigate('/dashboard?section=billing')} className="hidden md:inline-flex h-7 px-3 text-[11px] bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-full">
              <Zap className="w-2.5 h-2.5 mr-1 text-orange-400" /> Buy credits
            </Button>
          ) : (
            <Button size="sm" onClick={() => navigate('/Pricing')} className="hidden md:inline-flex h-7 px-3 text-[11px] bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-full">
              <Zap className="w-2.5 h-2.5 mr-1 text-orange-400" /> Upgrade
            </Button>
          )}
          {/* GitHub — the answer to the lock-in objection. Only with a site to
              commit; during the plan there is nothing to send. */}
          {effectiveWebsiteId && (
            <button
              onClick={() => { setShowGitHub(v => !v); setShowPublishPanel(false); }}
              title="Commit this site to your own GitHub repository"
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors"
            >
              <Github className="w-3 h-3" /> GitHub
            </button>
          )}

          {/* Share — opens the slide-over that puts the site online and shows
              its link (Feature 7). It said "Publish", and people who had just
              built a site went looking for their link without knowing that
              was where it lived. Only with a site to share: on the dashboard
              with none open it could only fail. */}
          {effectiveWebsiteId && (
            <Button
              size="sm"
              // The panel slides over the site, so on a phone the site comes forward.
              onClick={() => { setShowPublishPanel(v => !v); setMobileView('workbench'); }}
              className="h-7 px-4 gap-1.5 text-[11px] bg-orange-500 hover:bg-orange-500/90 text-ink rounded-full font-bold [&_svg]:size-3.5"
            >
              <Link2 /> Share
            </Button>
          )}

          {isMobile && (
            <PhoneMoreMenu
              hasSite={!!effectiveWebsiteId}
              onCredits={() => { setShowCreditsPanel(true); openChat(); }}
              onUpgrade={() => navigate('/Pricing')}
              onGitHub={() => { setRightTab(t => (t === 'dashboard' ? 'preview' : t)); setMobileView('workbench'); setShowGitHub(true); setShowPublishPanel(false); }}
            />
          )}
        </div>
      </div>

      {/* ══ PROGRESS ═════════════════════════════════════════════════════════ */}
      {/* Full width and above the panels, so it stays in view on whichever tab
          the user wanders off to while the site is being written. */}
      {/* Not on a phone: there it is the floating bar over the site, and a
          full-width strip here was one more row of chrome above it. */}
      <AnimatePresence>
        {isGenerating && !isMobile && <GenerationProgressBar progress={progress} />}
      </AnimatePresence>

      {/* ══ PANELS ═══════════════════════════════════════════════════════════ */}
      {/* Side-by-side, resizable columns on a desktop; one pane at a time on a
          phone (see PhoneStack). The contents are the same either way. */}
      <Group {...groupProps}>

        {/* ── LEFT PANEL (TOP BAND ON MOBILE): CHAT ───────────────────────
            Hidden on the Dashboard: the generator stream is about the build in
            progress, and the dashboard is not that. Leaving it there gave the
            console a permanently empty column. */}
        {showChat && (
        <ChatPane {...chatPaneProps}>

          <AnimatePresence>
            {showChatHistory && (
              <ChatHistoryPanel
                messages={messages}
                onJump={jumpToMessage}
                onClose={() => setShowChatHistory(false)}
              />
            )}
          </AnimatePresence>

          {/* Credits panel slide-over (Feature 2). Opened from the credits
              counter in the top bar; it slides over this pane. */}
          <AnimatePresence>
            {showCreditsPanel && (
              <CreditsPanel
                credits={credits}
                planLimit={planLimit}
                subscriptionTier={subscriptionTier}
                profile={profileRow}
                onClose={() => setShowCreditsPanel(false)}
                navigate={navigate}
              />
            )}
          </AnimatePresence>

          {/* Panel header */}
          <div className="h-10 shrink-0 flex items-center justify-between px-3 border-b border-white/5 bg-zinc-950/60">
            {/* The toggle took the place of a credits button whose PanelLeft
                glyph read as "toggle sidebar" and did something else; the
                credits counter in the top bar opens that panel. */}
            <div className="flex items-center gap-2 min-w-0">
              {!isMobile && <ChatToggle collapsed={false} onToggle={toggleChat} />}
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono tracking-widest uppercase border border-zinc-800 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse shadow-[0_0_8px_rgba(249,115,22,0.8)]" />
                Chat Assistant
              </div>
            </div>
            {/* One history control, not two. A "History" pill for version
                history sat beside this icon and read as a duplicate of it;
                versions are listed and reverted under Dashboard → Versions. */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => setShowChatHistory(v => !v)}
                title="Chat history"
                aria-label="Chat history"
                aria-expanded={showChatHistory}
                className={`shrink-0 flex items-center justify-center w-6 h-6 rounded-lg border transition-colors ${
                  showChatHistory
                    ? 'border-orange-500/40 bg-orange-500/10 text-orange-400'
                    : 'border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-800 hover:border-zinc-700'
                }`}
              >
                <MessagesSquare className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Chat messages (Feature 9: scroll container with ref) */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar relative">
            {messages.map(msg =>
              msg.role === 'user'
                ? <UserBubble key={msg.id} msgId={msg.id} content={msg.content} timestamp={msg.timestamp} userInitial={userInitial} highlighted={flashMsgId === msg.id} />
                : <AIBubble   key={msg.id} content={msg.content} timestamp={msg.timestamp} />
            )}

            {/* Plan mode for a new site — before any tokens are spent on a
                build. On a desktop the plan fills the workbench and this card
                says so; on a phone the chat IS the screen, so the whole plan
                is here. */}
            {inPlanStage && (isMobile ? (
              <PlanBlueprint
                layout="column"
                plan={pendingPlan}
                status={planStatus}
                error={planError}
                prompt={buildPromptRef.current || prompt}
                credits={credits}
                canUndo={planHistory.length > 0}
                onChange={setPendingPlan}
                onBuild={buildFromPlan}
                onSkip={buildWithoutPlan}
                onRetry={() => draftPlan()}
                onRevise={(feedback) => draftPlan({ feedback })}
                onUndo={undoPlan}
              />
            ) : (
              <PlanChatCard
                status={planStatus}
                plan={pendingPlan}
                error={planError}
                onBuild={buildFromPlan}
                onRetry={() => draftPlan()}
                onSkip={buildWithoutPlan}
              />
            ))}

            {/* Plan mode for a change to this site: what will change, page by
                page, before the credit is spent. */}
            {editPlan && !isGenerating && (
              <EditPlanCard
                editPlan={editPlan}
                credits={credits}
                onApply={applyEditPlan}
                onDiscard={discardEditPlan}
                onRetry={() => planChange(editPlan.request, { echo: false, fresh: true })}
              />
            )}

            {/* Live streaming — the build's plan breakdown, or for an edit, the
                edit itself */}
            {(isGenerating || (streamedText && !effectiveWebsiteId)) && (
              firstBuild ? (
                <GenerationPlanView
                  progress={progress}
                  plan={approvedPlan}
                  prompt={prompt || messages[0]?.content || 'your website'}
                />
              ) : (
                <EditProgressView
                  progress={progress}
                  request={[...messages].reverse().find(m => m.role === 'user')?.content || 'your change'}
                />
              )
            )}

            {/* Success bubble — gated on `completed`, which is set only by the
                server's `complete` event, i.e. only once the site is SAVED.
                It used to be `!isGenerating && effectiveWebsiteId`, but the id
                arrives with `started`, before any token: a run that died mid
                stream rendered this banner anyway, directly above the error
                that killed it. Users then published a row with no page in it
                and got "This site has no content yet." on their live URL. */}
            <AnimatePresence>
              {completed && !isGenerating && effectiveWebsiteId && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 px-3 py-2.5 min-w-0">
                  <img src="/images/builderslab-logo.png" alt="BuildersLab" className="w-7 h-7 object-contain shrink-0" />
                  {/* The link rides at the end of the second line, and drops
                      below it when the column is too narrow. Nothing truncates:
                      at ~270px "Built in 9m 11s · 5 p…" hid the page count,
                      and the column's default is narrower still now. */}
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-white leading-tight">
                      {firstBuild ? 'Your website is ready' : 'Your changes are in'}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                      <p className="text-[11px] text-zinc-400 tabular-nums">
                        {lastRunMs != null ? `${firstBuild ? 'Built' : 'Updated'} in ${formatDuration(lastRunMs)}` : 'Saved'}
                        {pages.length > 0 && ` · ${pages.length} page${pages.length === 1 ? '' : 's'}`}
                      </p>
                      <button
                        onClick={() => setRightTab('dashboard')}
                        className="shrink-0 inline-flex items-center gap-0.5 text-[11.5px] font-medium text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        Dashboard <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    {/* The link is the first thing people look for once a site
                        is built, and the only way to it was a top-bar button
                        called "Publish". This opens the same panel, which puts
                        the site online and shows the link with a Copy button. */}
                    <button
                      onClick={() => { setShowPublishPanel(true); setMobileView('workbench'); }}
                      className="mt-2 inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-orange-500 hover:bg-orange-500/90 text-ink text-[11.5px] font-semibold transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Get your link
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error bubble */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }} className="flex items-end gap-2.5 min-w-0 overflow-hidden">
                  <div className="w-8 h-8 rounded-full bg-red-500/15 border border-red-500/25 flex items-center justify-center shrink-0 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-2xl rounded-bl-sm px-4 py-3 min-w-0 flex-1 overflow-hidden">
                    <p className="text-red-400 text-[13px] font-medium mb-2.5 break-all">{error}</p>
                    <Button size="sm" onClick={retryLastRun} className="h-7 px-3 text-xs bg-red-500 hover:bg-red-400 text-white rounded-full">
                      Try Again
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Ended without being saved.
                The counterpart to the success bubble: a run that streamed in
                this session, is no longer running, and never got `complete`.
                `streamedText` is what makes this specific to a run that
                happened here — simply opening an already-saved site leaves it
                empty, so this cannot fire on one. Without this branch such a
                run now renders nothing at all, which is quieter than the wrong
                banner but still leaves the user believing they have a site.

                `stopped` is excluded because a stop is not a failure: the
                server keeps whatever had been written and leaves it at
                'completed', so the run really is saved. Without that exclusion
                pressing Stop answered "your site was never saved" directly
                under handleStop's own "what was built so far is saved", which
                reads as the button having broken something. */}
            <AnimatePresence>
              {!isGenerating && !completed && !error && !stopped && streamedText && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }} className="flex items-end gap-2.5 min-w-0 overflow-hidden">
                  <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/25 flex items-center justify-center shrink-0 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl rounded-bl-sm px-4 py-3 min-w-0 flex-1 overflow-hidden">
                    <p className="text-amber-300 text-[13px] font-medium mb-1 break-words">This run ended before your site was saved.</p>
                    <p className="text-zinc-400 text-xs mb-3 leading-relaxed break-words">What you can see in the preview was never written to your account, so it cannot be published yet. Running it again will save it.</p>
                    <Button size="sm" onClick={retryLastRun} className="h-7 px-3 text-xs bg-amber-500 hover:bg-amber-400 text-white rounded-full">
                      Try Again
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Clearing faults, stated as progress rather than as a fault list.

                This used to render every finding with a "Fix them" button, and
                a fifteen-item list is a bug report, not a website — the button
                also spent another credit and came back having broken the hero
                image. Repair now happens on the server before the site is
                saved, so by the time anything reaches this panel it is done.

                What remains is shown only as a count, and only when something
                could not be cleared: naming a defect the user cannot act on
                helps nobody, and a follow-up in their own words works better
                than a list of ours. */}
            <AnimatePresence>
              {repairing && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-2.5 text-xs text-zinc-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400 shrink-0" />
                  Tidying up a few loose ends…
                </motion.div>
              )}
            </AnimatePresence>

            {/* What the repair could not finish, said in the owner's terms and
                named — "Shop: the product grid is empty" — with one way to act
                on it. "Fix these" writes a precise request into the message box;
                it never sends one. A grey count ("3 sections still need work —
                tell me what they should show") named nothing, so there was
                nothing to tell. Three at most, so it stays a note and not a
                bug report. */}
            <AnimatePresence>
              {completed && !isGenerating && issues?.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/70 px-3 py-2.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                    <p className="text-[12.5px] font-medium text-zinc-200">
                      {issues.length === 1 ? 'One thing left to finish' : `${issues.length} things left to finish`}
                    </p>
                  </div>
                  <ul className="mt-1.5 space-y-0.5 pl-[22px]">
                    {issues.slice(0, 3).map((issue, i) => (
                      <li key={i} className="text-[11.5px] leading-snug text-zinc-400 break-words list-disc marker:text-zinc-600">
                        {describeIssue(issue).text}
                      </li>
                    ))}
                    {issues.length > 3 && (
                      <li className="text-[11px] text-zinc-500 list-none">and {issues.length - 3} more</li>
                    )}
                  </ul>
                  <button
                    onClick={() => { setFollowUp(fixRequest(issues)); textareaRef.current?.focus(); }}
                    title="Writes the request into the message box — sending it uses one credit"
                    className="mt-2 inline-flex items-center gap-0.5 text-[11.5px] font-medium text-orange-400 hover:text-orange-300 transition-colors"
                  >
                    Fix these <ChevronRight className="w-3 h-3" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Out-of-credits bubble */}
            <AnimatePresence>
              {credits === 0 && !isGenerating && (
                <motion.div initial={{ opacity: 0, y: 12, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ type: 'spring', stiffness: 300, damping: 24 }} className="flex items-end gap-2.5 min-w-0 overflow-hidden">
                  <div className="w-8 h-8 rounded-full bg-orange-500/15 border border-orange-500/25 flex items-center justify-center shrink-0 mb-2">
                    <Zap className="w-3.5 h-3.5 text-orange-400" />
                  </div>
                  {/* Said per plan: "your free credits" and "upgrade to a paid
                      plan" read as an error to someone already paying for Pro,
                      whose way forward is a credit pack. */}
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl rounded-bl-sm px-4 py-3 min-w-0 flex-1 overflow-hidden">
                    {canBuyCreditPacks(subscriptionTier) ? (
                      <>
                        <p className="text-orange-300 text-[13px] font-semibold mb-1 break-words">You've used this month's credits.</p>
                        <p className="text-zinc-400 text-xs mb-3 leading-relaxed break-words">Top up with a credit pack to keep building now — bought credits never expire.</p>
                        <Button size="sm" onClick={() => navigate('/dashboard?section=billing')} className="h-7 px-3 text-xs bg-orange-500 hover:bg-orange-400 text-ink rounded-full">
                          <Zap className="w-3 h-3 mr-1.5" /> Buy credits
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-orange-300 text-[13px] font-semibold mb-1 break-words">
                          {isPaidPlan ? "You've used this month's credits." : "You've used all your free credits."}
                        </p>
                        <p className="text-zinc-400 text-xs mb-3 leading-relaxed break-words">
                          {isPaidPlan
                            ? 'Upgrade to Pro for more credits every month, and top up any time.'
                            : 'Upgrade to a paid plan to keep building.'}
                        </p>
                        <Button size="sm" onClick={() => navigate('/Pricing')} className="h-7 px-3 text-xs bg-orange-500 hover:bg-orange-400 text-ink rounded-full">
                          <Zap className="w-3 h-3 mr-1.5" /> Upgrade Now
                        </Button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div ref={chatEndRef} />
          </div>

          {/* Feature 9: "Latest messages" sticky button */}
          <AnimatePresence>
            {showLatestBtn && (
              <motion.button
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                onClick={() => {
                  // Re-arm the pin as well as scrolling: without this the view
                  // jumps to the bottom once and then stops following the run.
                  pinnedRef.current = true;
                  setShowLatestBtn(false);
                  chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }}
                className="absolute bottom-[140px] left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 text-xs text-zinc-300 shadow-lg z-10"
              >
                <Clock className="w-3 h-3 text-orange-400" /> Latest messages
              </motion.button>
            )}
          </AnimatePresence>

          {/* Input */}
          <div className={`shrink-0 border-t border-white/5 p-2 md:p-3 transition-opacity duration-200 ${isGenerating ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
            {credits === 0 ? (
              <div className="flex items-center gap-3 bg-zinc-900/80 border border-orange-500/25 rounded-2xl px-4 py-3">
                <div className="w-7 h-7 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                  <Zap className="w-3.5 h-3.5 text-orange-400" />
                </div>
                {canBuyCreditPacks(subscriptionTier) ? (
                  <>
                    <p className="text-xs text-zinc-400 flex-1 leading-relaxed">No credits left. Top up to keep building — bought credits never expire.</p>
                    <Button size="sm" onClick={() => navigate('/dashboard?section=billing')} className="h-7 px-3 text-[11px] bg-orange-500 hover:bg-orange-400 text-ink rounded-full shrink-0">Buy credits</Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-zinc-400 flex-1 leading-relaxed">No credits left. Upgrade to continue generating.</p>
                    <Button size="sm" onClick={() => navigate('/Pricing')} className="h-7 px-3 text-[11px] bg-orange-500 hover:bg-orange-400 text-ink rounded-full shrink-0">Upgrade</Button>
                  </>
                )}
              </div>
            ) : (
              <>
                <AnimatePresence>
                  {attachedFile && (
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} className="flex items-center gap-2 mb-2 px-1">
                      <div className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-zinc-300">
                        {attachedImage ? <ImageIcon className="w-3 h-3 text-blue-400" /> : <Paperclip className="w-3 h-3 text-zinc-500" />}
                        <span className="max-w-[120px] truncate">{attachedFile.name}</span>
                        {attachedImage && <span className="text-blue-400 text-[10px]">· vision</span>}
                        <button onClick={() => { setAttachedFile(null); setAttachedImage(null); }} className="text-zinc-500 hover:text-white ml-1"><X className="w-3 h-3" /></button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-end gap-2 bg-zinc-900 border border-zinc-800 rounded-2xl px-3 py-1.5 md:py-2.5 focus-within:border-zinc-600 transition-colors">
                  <div className="flex items-center gap-0.5 shrink-0 pb-0.5">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                    <button onClick={() => fileInputRef.current?.click()} className="p-1.5 text-zinc-500 hover:text-zinc-300 rounded-lg hover:bg-zinc-800 transition-colors">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button onClick={handleVoice} className={`p-1.5 rounded-lg transition-colors relative ${isRecording ? 'text-red-400' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}>
                      {isRecording && <span className="absolute inset-0 rounded-lg bg-red-500/20 animate-ping" />}
                      {isRecording ? <MicOff className="w-4 h-4 relative z-10" /> : <Mic className="w-4 h-4" />}
                    </button>
                    {/* Plan mode for changes: see what will change, free,
                        before a credit is spent. Only once there is a site. */}
                    {siteInView && !inPlanStage && (
                      <button
                        onClick={() => setEditPlanMode(!editPlanMode)}
                        aria-pressed={editPlanMode}
                        aria-label="Plan mode"
                        title={editPlanMode
                          ? 'Plan mode is on: changes are planned first, free, before they are made'
                          : 'Plan mode: see the changes before they are made (free)'}
                        className={`p-1.5 rounded-lg transition-colors ${editPlanMode ? 'text-orange-400 bg-orange-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}
                      >
                        <ListChecks className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Textarea
                    ref={textareaRef}
                    value={followUp}
                    // The same cap as the first prompt, from limits.json. This
                    // box had none, so a longer edit was cut off server-side
                    // with no sign anything was lost.
                    onChange={e => setFollowUp(e.target.value.slice(0, MAX_PROMPT))}
                    onKeyDown={handleKeyDown}
                    aria-label="Message"
                    // Says what sending will do, in as few words as possible —
                    // a full sentence wraps to three lines in a narrow band,
                    // pushing the stream further out of view.
                    placeholder={inPlanStage
                      ? 'Change the plan…'
                      : editPlan && editPlan.status !== 'drafting'
                        ? 'Adjust the plan…'
                        : editPlanMode && siteInView ? 'Plan a change…' : ''}
                    rows={1}
                    // 16px on a phone: iOS Safari zooms the whole page into any
                    // text box set smaller, and the layout jumps with it.
                    className="flex-1 bg-transparent border-none resize-none text-base md:text-sm text-white placeholder-zinc-600 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 min-h-[24px] max-h-24 leading-relaxed shadow-none"
                    style={{ height: 'auto' }}
                  />
                  {isGenerating ? (
                    <button
                      onClick={handleStop}
                      title="Stop generating"
                      aria-label="Stop generating"
                      className="shrink-0 w-8 h-8 rounded-xl bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      <span className="block w-2.5 h-2.5 rounded-[3px] bg-white" />
                    </button>
                  ) : (
                    <button onClick={handleFollowUp} disabled={!followUp.trim()} aria-label="Send" className="shrink-0 w-8 h-8 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all shadow-[0_0_12px_rgba(249,115,22,0.3)] active:scale-95">
                      <Send className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                </div>
                {(followUp.length > 0 || inPlanStage || (editPlanMode && siteInView)) && (
                  <div className="px-1 pt-1 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-orange-300/80 truncate">
                      {inPlanStage ? 'Plan mode · changing the plan is free'
                        : editPlanMode && siteInView ? 'Plan mode · you approve changes before they are made' : ''}
                    </span>
                    {followUp.length > 0 && (
                      <span className={`text-[10px] tabular-nums font-medium transition-colors shrink-0 ${promptCountTone(followUp.length)}`}>
                        {followUp.length} / {MAX_PROMPT}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

        </ChatPane>
        )}

        {/* ── Resize handle (desktop only; a phone shows one pane) ────────── */}
        {showChat && !isMobile && (
        <ResizableHandle
          withHandle
          className="bg-zinc-900 hover:bg-orange-500/40 transition-colors active:bg-orange-500 w-1.5 cursor-col-resize"
        />
        )}

        {/* ── RIGHT PANEL: the site, its code, or the dashboard ─────────────── */}
        <WorkPane {...workPaneProps}>

          {/* A build in progress while a phone shows the site: progress, the
              way back to the task list, and Stop. */}
          {isMobile && isGenerating && (
            <PhoneBuildPill progress={progress} onOpen={openChat} onStop={handleStop} />
          )}

          {/* Publish panel slide-over (Feature 7) */}
          <AnimatePresence>
            {showGitHub && (
              <GitHubPanel
                websiteId={effectiveWebsiteId}
                defaultRepo={(websiteCode.title || 'my-website').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}
                onClose={() => setShowGitHub(false)}
              />
            )}
            {showPublishPanel && (
              <PublishPanel
                websiteId={effectiveWebsiteId}
                onClose={() => setShowPublishPanel(false)}
                onAddOrderButtons={runRefinement}
              />
            )}
          </AnimatePresence>

          {/* Plan mode: a new site's plan fills the workbench until Build. Laid
              over the preview rather than replacing it, so the preview frame
              underneath is already loaded when the first markup arrives. */}
          {inPlanStage && !isMobile && (
            <div className="absolute inset-0 z-20 bg-zinc-950 overflow-y-auto custom-scrollbar">
              {chatCollapsed && (
                <div className="sticky top-0 z-30 h-0">
                  <span className="absolute top-3 left-3 flex"><ChatToggle collapsed onToggle={toggleChat} /></span>
                </div>
              )}
              <PlanBlueprint
                layout="wide"
                plan={pendingPlan}
                status={planStatus}
                error={planError}
                prompt={buildPromptRef.current || prompt}
                credits={credits}
                canUndo={planHistory.length > 0}
                onChange={setPendingPlan}
                onBuild={buildFromPlan}
                onSkip={buildWithoutPlan}
                onRetry={() => draftPlan()}
                onRevise={(feedback) => {
                  setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: feedback, timestamp: new Date() }]);
                  draftPlan({ feedback });
                }}
                onUndo={undoPlan}
              />
            </div>
          )}

          {/* Preview toolbar */}
          {rightTab === 'preview' && (
            <div className="h-10 shrink-0 flex items-center justify-between px-2 md:px-3 gap-2 border-b border-white/5 bg-zinc-950/80">
              <div className="flex items-center gap-1.5 min-w-0">
                {chatCollapsed && !isMobile && (
                  <span className="mr-1 flex">
                    <ChatToggle collapsed onToggle={toggleChat} busy={isGenerating} />
                  </span>
                )}
                <span className={`w-2 h-2 rounded-full shrink-0 transition-all duration-300 ${isGenerating ? 'bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.7)]' : 'bg-zinc-600'}`} />
                {/* The label is the first thing to go: the page tabs beside it
                    are navigation, and the status dot already says "live". */}
                <span className="hidden sm:inline text-xs text-zinc-500 shrink-0">Live Preview</span>

                {/* Page tabs — the preview's own address bar. Clicking a link
                    inside the site moves this too, so the two stay in step. */}
                {/* During a first build every planned page is here from the
                    start: the one being written pulses, the ones not reached yet
                    are hollow, and any of them can be opened to see its frame. */}
                {/* On a phone the pages are a native picker: a row of tabs had
                    room for "Home" and half of "Collection", and none for the
                    rest of a five-page site. */}
                {previewPages.length > 1 && isMobile && (
                  <label className="relative ml-1 min-w-0 flex items-center">
                    <span className="sr-only">Page</span>
                    <select
                      value={activePage}
                      onChange={(e) => choosePage(e.target.value)}
                      className="appearance-none min-w-0 max-w-[40vw] truncate bg-zinc-800 text-white text-base rounded-lg pl-2.5 pr-7 py-1 border border-zinc-700 focus:outline-none focus:border-zinc-500"
                    >
                      {previewPages.map(p => {
                        const state = !wireframe ? null
                          : p.path === livePath ? ' · writing'
                          : p.html ? '' : ' · not yet';
                        return <option key={p.path} value={p.path}>{pageLabel(p.path)}{state || ''}</option>;
                      })}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 w-3.5 h-3.5 text-zinc-400" />
                  </label>
                )}
                {previewPages.length > 1 && !isMobile && (
                  <div className="flex items-center gap-1 ml-2 overflow-x-auto custom-scrollbar min-w-0">
                    <div className="w-px h-4 bg-zinc-800 shrink-0 mr-1" />
                    {previewPages.map(p => {
                      const state = !wireframe ? null
                        : p.path === livePath ? 'writing'
                        : p.html ? 'written' : 'waiting';
                      return (
                        <button
                          key={p.path}
                          onClick={() => choosePage(p.path)}
                          title={state === 'waiting' ? `${p.path} — not written yet` : state === 'writing' ? `${p.path} — being written` : p.path}
                          className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                            activePage === p.path
                              ? 'bg-zinc-700 text-white'
                              : state === 'waiting'
                                ? 'text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800'
                                : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800'
                          }`}
                        >
                          {state === 'writing' && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse shadow-[0_0_6px_rgba(249,115,22,0.8)]" />}
                          {state === 'waiting' && <span className="w-1.5 h-1.5 rounded-full border border-current opacity-70" />}
                          {pageLabel(p.path)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* The device switcher previews other form factors, which needs
                    room this pane does not have on a phone — and the phone is
                    already showing the mobile rendering. Fullscreen stays. */}
                <div className="hidden md:flex items-center gap-1">
                  {[{ view: 'desktop', Icon: Monitor }, { view: 'tablet', Icon: Tablet }, { view: 'mobile', Icon: Smartphone }].map(({ view, Icon }) => (
                    <button key={view} onClick={() => setDeviceView(view)} className={`p-1.5 rounded-lg transition-colors ${deviceView === view ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                  <div className="w-px h-4 bg-zinc-800 mx-1" />
                </div>

                {/* Visual editing — free, instant, no model in the loop. Gated
                    to paid plans only because PUT /websites/:id/code is, so an
                    unavailable button would fail at save time instead. */}
                {canVisualEdit && websiteCode.html && (
                  visualEdit ? (
                    <div className="flex items-center gap-1 mr-1">
                      <Button
                        size="sm" onClick={commitVisualEdits} disabled={!visualDirty || savingVisual}
                        className="h-7 px-3 text-[11px] bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg disabled:opacity-50"
                      >
                        {savingVisual ? 'Saving…' : visualDirty ? 'Save edits' : 'No changes'}
                      </Button>
                      <Button
                        size="sm" variant="ghost" onClick={discardVisualEdits} disabled={savingVisual}
                        className="h-7 px-2 text-[11px] text-zinc-400 hover:text-white rounded-lg"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setVisualEdit(true)}
                      title="Click any element to edit it — uses no credits"
                      className="flex items-center gap-1.5 px-2.5 py-1 mr-1 rounded-lg text-[11px] font-medium border border-zinc-800 text-zinc-400 hover:text-orange-400 hover:border-orange-500/40 transition-colors"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  )
                )}

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg">
                      <Maximize2 className="w-3.5 h-3.5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`max-w-[100vw] w-screen h-screen m-0 p-0 bg-snow border-none rounded-none outline-none ${FULLSCREEN_CLOSE}`}>
                    <DialogTitle className="sr-only">Website Preview</DialogTitle>
                    <div className="relative w-full h-full">
                      <LivePreviewFrame
                        pages={previewPages} css={websiteCode.css} js={websiteCode.js} icon={websiteCode.icon}
                        activePath={activePage} onNavigate={choosePage} onBlockedLink={handleBlockedLink}
                        isGenerating={isGenerating} wireframe={wireframe}
                        title="Preview"
                        className="w-full h-full border-none"
                      />
                      <DialogTrigger asChild>
                        <Button className="absolute bottom-6 mx-auto left-0 right-0 w-fit bg-zinc-950 text-white shadow-2xl rounded-full px-8 hover:bg-zinc-800">Close Fullscreen</Button>
                      </DialogTrigger>
                    </div>
                  </DialogContent>
                </Dialog>
                {/* A menu rather than a jump to the Code tab: downloading is
                    what most people press this for, and it used to take a tab
                    switch and then finding an unlabelled arrow. Viewing the
                    code is still the last item. */}
                <DownloadMenu
                  onDownload={handleDownloadZip}
                  canExport={canExportCode}
                  hasSite={!!websiteCode.html && !isGenerating}
                  onViewCode={() => setRightTab('code')}
                  unlock={unlock}
                >
                  {/* Worded on a phone too. It was a bare "</>" there to leave
                      the page picker room, and site owners did not read that
                      as "download" — the picker truncates instead. The icon is
                      what goes on a phone, not the words. */}
                  <Button size="sm" className="h-7 px-2.5 sm:px-3 text-[11px] whitespace-nowrap bg-orange-500 hover:bg-orange-400 data-[state=open]:bg-orange-400 text-ink rounded-lg">
                    <Download className="hidden sm:inline w-3 h-3 mr-1.5" />Download Code <ChevronDown className="w-3 h-3 ml-0.5 sm:ml-1 opacity-80" />
                  </Button>
                </DownloadMenu>
              </div>
            </div>
          )}

          {/* Preview content */}
          {rightTab === 'preview' && (
            <div className="flex-1 overflow-hidden flex items-center justify-center bg-zinc-950 relative">
              {/* The frame is mounted from the start, even while the skeleton
                  covers it, so its shell and the Tailwind runtime are loaded and
                  ready by the time the first markup arrives. */}
              {effectiveDeviceView === 'desktop' ? (
                <div className="w-full h-full bg-snow">
                  <LivePreviewFrame
                    pages={previewPages} css={websiteCode.css} js={websiteCode.js} icon={websiteCode.icon}
                    activePath={activePage} onNavigate={choosePage} onBlockedLink={handleBlockedLink}
                    isGenerating={isGenerating} wireframe={wireframe}
                    ref={previewRef} editMode={visualEdit}
                    onSelect={setSelectedEl} onEditDirty={() => setVisualDirty(true)}
                    title="Live Website Preview"
                    className="w-full h-full border-none"
                  />
                </div>
              ) : effectiveDeviceView === 'tablet' ? (
                <div className="h-full w-full flex items-center justify-center bg-zinc-950 p-2 md:p-6 overflow-auto">
                  {/* max-w-full keeps the bezel inside the viewport on a phone;
                      the fixed width is a target, not a floor. */}
                  <div className="relative flex flex-col h-full max-h-[900px] w-[768px] max-w-full shrink bg-zinc-800 rounded-[24px] border-2 border-zinc-700 shadow-[0_24px_80px_rgba(0,0,0,0.35)] overflow-hidden">
                    <div className="h-6 bg-zinc-800 flex items-center justify-center gap-3 shrink-0 border-b border-zinc-700/50">
                      <div className="w-3 h-3 rounded-full bg-zinc-600" /><div className="w-20 h-1.5 bg-zinc-600 rounded-full" />
                    </div>
                    <div className="flex-1 overflow-hidden bg-snow">
                      <LivePreviewFrame
                        pages={previewPages} css={websiteCode.css} js={websiteCode.js} icon={websiteCode.icon}
                        activePath={activePage} onNavigate={choosePage} onBlockedLink={handleBlockedLink}
                        isGenerating={isGenerating} wireframe={wireframe}
                        ref={previewRef} editMode={visualEdit}
                        onSelect={setSelectedEl} onEditDirty={() => setVisualDirty(true)}
                        title="Tablet preview"
                        className="w-full h-full border-none"
                      />
                    </div>
                    <div className="h-5 bg-zinc-800 flex items-center justify-center shrink-0 border-t border-zinc-700/50">
                      <div className="w-16 h-1.5 bg-zinc-600 rounded-full" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-zinc-950 p-2 md:p-6 overflow-auto">
                  <div className="relative flex flex-col h-full max-h-[812px] w-[390px] max-w-full shrink bg-zinc-800 rounded-[44px] border-[3px] border-zinc-700 shadow-[0_24px_80px_rgba(0,0,0,0.35)] overflow-hidden">
                    <div className="h-10 bg-zinc-800 flex items-center justify-center shrink-0">
                      <div className="w-28 h-6 bg-zinc-900 rounded-full flex items-center justify-center gap-2 border border-zinc-700/80">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-600" /><div className="w-8 h-1.5 bg-zinc-600 rounded-full" />
                      </div>
                    </div>
                    <div className="flex-1 overflow-hidden bg-snow">
                      <LivePreviewFrame
                        pages={previewPages} css={websiteCode.css} js={websiteCode.js} icon={websiteCode.icon}
                        activePath={activePage} onNavigate={choosePage} onBlockedLink={handleBlockedLink}
                        isGenerating={isGenerating} wireframe={wireframe}
                        ref={previewRef} editMode={visualEdit}
                        onSelect={setSelectedEl} onEditDirty={() => setVisualDirty(true)}
                        title="Mobile preview"
                        className="w-full h-full border-none"
                      />
                    </div>
                    <div className="h-8 bg-zinc-800 flex items-center justify-center shrink-0">
                      <div className="w-28 h-1.5 bg-zinc-600 rounded-full" />
                    </div>
                  </div>
                </div>
              )}

              {/* Properties panel for the current selection. Docked over the
                  frame rather than beside it, so turning edit mode on does not
                  reflow the preview the user is working on. */}
              {visualEdit && (
                <div className="absolute bottom-0 left-0 right-0 z-20 shadow-[0_-8px_24px_rgba(0,0,0,.5)]">
                  <VisualEditBar
                    element={selectedEl}
                    onSelectElement={selectFromPanel}
                    onChange={() => setVisualDirty(true)}
                    onClose={() => selectFromPanel(null)}
                  />
                </div>
              )}

              {/* Covers the frame until there is markup worth showing, then
                  fades off it. An overlay rather than a branch, so revealing the
                  page never unmounts (and so reloads) the frame underneath.
                  A first build with a plan or an outline skips it: the frame
                  itself is already drawing the site's wireframe. */}
              <AnimatePresence>
                {!websiteCode.html && !wireframe && (
                  <motion.div
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="absolute inset-0 z-10 bg-zinc-950"
                  >
                    <BuildingSkeleton progress={progress} isGenerating={isGenerating} />
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          )}

          {/* The site's own dashboard: overview, versions, settings, activity
              and selling it. Account things live on the account dashboard. */}
          {rightTab === 'dashboard' && siteInView && (
            <SiteDashboard
              websiteId={effectiveWebsiteId}
              websiteCode={{ ...websiteCode, pages }}
              versionHistory={versionHistory}
              onRevert={handleRevert}
              onDownloadZip={handleDownloadZip}
              canExport={canExportCode}
              unlock={unlock}
              navigate={navigate}
              onShare={() => { setShowPublishPanel(true); setMobileView('workbench'); }}
              onTitleSaved={(t) => { setTopTitleVal(t); setWebsiteCode(c => ({ ...c, title: t })); }}
              onIconSaved={(icon) => setWebsiteCode(c => ({ ...c, icon }))}
            />
          )}

          {rightTab === 'code' && (
            <CodeTab
              websiteCode={websiteCode}
              isGenerating={isGenerating}
              isPaidPlan={isPaidPlan}
              canExport={canExportCode}
              onBuySite={handleBuySite}
              buyingSite={buyingSite}
              sitePrice={sitePrice}
              planPrice={planPrice}
              unlock={unlock}
              chatToggle={chatCollapsed && !isMobile && (
                <ChatToggle collapsed onToggle={toggleChat} busy={isGenerating} />
              )}
              navigate={navigate}
              onCopy={handleCopy}
              copySuccess={copySuccess}
              onDownloadZip={handleDownloadZip}
              websiteId={effectiveWebsiteId}
              onSaveCode={saveCodeDrafts}
              drafts={codeDrafts}
              setDrafts={setCodeDrafts}
              saveState={codeSaveState}
            />
          )}
        </WorkPane>
      </Group>

      {isMobile && !typing && hasWork && (
        <PhoneTabBar
          tabs={phoneTabs}
          active={rightTab === 'dashboard' ? 'dashboard' : mobileView === 'chat' ? 'chat' : rightTab}
          onSelect={selectPhoneTab}
        />
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes shimmer { 100% { transform: translateX(200%); } }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #27272a; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #3f3f46; }
      `}} />
    </motion.div>
  );
}
